import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchJob, updateJobStops, startJob, completeJob, guardReleaseJob, updateJob, markPackageNotOnFlight, unmarkPackageNotOnFlight, logPhoto, jobDisplayNumber } from "../../services/firestore";
import { guardWorkOrder } from "../../services/guardStopOrder";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { uploadGuardPhoto } from "../../services/storage";
import ScanPackageModal from "../../components/guard/ScanPackageModal";
import PlacesAutocomplete from "../../components/PlacesAutocomplete";
import PodCropper from "../../components/PodCropper";
import BarcodeScanner from "../../components/BarcodeScanner";
import { hawbLabel, hawbNumLabel, hawbInputMode } from "../../services/hawbLabel";
import { stopCoveredByGuard } from "../../services/billing";
import { formatPhone } from "../../utils/phone";
import "./GuardLayout.css";

// Metadata for the owner's live photo feed, provided once per job so the deeply
// nested PhotoCapture can log photos (with companyId — required by the rules)
// without prop-drilling through every component.
const PhotoMetaContext = React.createContext({});
// "Reorder stops" switch for the job screen — stop cards show their ↑ ↓
// arrows only while it's on, so the arrows don't crowd every card.
const ReorderModeContext = React.createContext(false);

// POD-photo requirement on DIRECT jobs. Orion is the DISPATCHER; of the
// brokers it sends (Malca, MWI, LALI, Iberjoya, EFFY, DAI, …) only MALCA
// requires a proof-of-delivery photo. Ferrari is its own dispatcher and
// also requires one. Everything else Orion dispatches intentionally does
// NOT capture a POD (it uses the checklist below, or just a "complete"
// tap) — this matches the owner-side billing rule. Do NOT add Orion
// brokers (MWI/LALI/Iberjoya/EFFY/DAI/…) here.
const PHOTO_BROKERS = ["malca","ferrari"];
// Brokers that use a confirmation checklist (not a POD) on direct jobs.
const CHECKLIST_BROKERS = ["dai","effy","lali","mwi","iberjoya","kallati","alberto"];

function brokerKey(name) { return (name || "").toLowerCase(); }

// Tappable address → opens Google Maps with driving directions.
// Works cross-platform: iOS opens Google Maps app (or Apple Maps via share),
// Android opens Google Maps app, desktop opens google.com/maps.
// Uses lat/lng if available (more precise), otherwise the address string.
// Render saved contacts for a stop, with tap-to-call / tap-to-email
// links. Returns null when there's nothing to show, so callers can drop
// it in unconditionally without conditional wrapping. Used inside the
// active stop card so the guard at the door can dial the recipient
// without bouncing back to text the owner.
function StopContactsCard({ stop, locationsById }) {
  // Merge every contact source we have for this stop, so the guard at
  // the door can always dial whoever they're picking up from or
  // delivering to:
  //   1. The job's own parsed contact (stop.contactName/contactPhone) —
  //      previously shown only on the My Jobs card, never in execution.
  //   2. The linked directory location's contacts[] list.
  //   3. The location's legacy single contactName/contactPhone.
  // Deduped by phone digits so the same person doesn't render twice.
  const loc = stop?.locationId ? locationsById?.[stop.locationId] : null;
  const merged = [];
  const seen = new Set();
  const push = (c) => {
    if (!c || (!c.name && !c.phone && !c.email)) return;
    const key = c.phone
      ? String(c.phone).replace(/\D/g, "")
      : `n:${(c.name || "").toLowerCase()}:${(c.email || "").toLowerCase()}`;
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(c);
  };
  push({ name: stop?.contactName, phone: stop?.contactPhone });
  (Array.isArray(loc?.contacts) ? loc.contacts : []).forEach(push);
  push({ name: loc?.contactName, phone: loc?.contactPhone });
  if (merged.length === 0) return null;
  return (
    <div style={{
      marginBottom: 10, padding: "8px 10px",
      background: "#eef4fa", border: "1px solid #c8dcee", borderRadius: 7,
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
        📇 Contacts
      </div>
      {merged.map((c, ci) => (
        <div key={ci} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: ci < merged.length - 1 ? 8 : 0 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: "#222" }}>{c.name || "Contact"}</span>
            {c.role && <span style={{ color: "#888", fontSize: 11 }}> · {c.role}</span>}
            {c.phone && (
              <div style={{ color: "#666", fontFamily: "monospace", fontSize: 11 }}>{formatPhone(c.phone)}</div>
            )}
            {c.email && (
              <a
                href={`mailto:${c.email}`}
                onClick={(e) => e.stopPropagation()}
                style={{ color: "#185FA5", textDecoration: "none", display: "block", fontSize: 11 }}
              >✉ {c.email}</a>
            )}
          </div>
          {c.phone && (
            <a
              href={`tel:${String(c.phone).replace(/[^0-9+]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
                background: "#185FA5", color: "#fff", borderRadius: 8,
                padding: "9px 14px", minHeight: 40, fontSize: 12, fontWeight: 700,
                textDecoration: "none",
              }}
            >📞 Call</a>
          )}
        </div>
      ))}
    </div>
  );
}

function AddressLink({ stop, style = {}, prominent = false }) {
  if (!stop?.address) return null;
  const dest = stop.lat && stop.lng
    ? `${stop.lat},${stop.lng}`
    : stop.address;
  const href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
  const baseStyle = prominent
    ? {
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "#E6F1FB", color: "#185FA5",
        padding: "6px 10px", borderRadius: 7,
        fontSize: 12, fontWeight: 600, textDecoration: "none",
      }
    : {
        fontSize: 12, color: "#185FA5",
        textDecoration: "underline", textDecorationColor: "#b9d0e6",
        display: "block",
      };
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ ...baseStyle, ...style }}
    >
      {prominent ? "🧭 Directions" : stop.address + "  🧭"}
    </a>
  );
}

// View-only stop card (bottom sheet) — opened by tapping a stop on My Jobs
// or on the job's start screen. Shows where to go, who to call and every
// package with its full number. Nothing here is editable: adding packages,
// POD photos and skips only happen after Start job, on the stop list.
export function StopPreviewSheet({ stop, number, packages = [], numLabel = "HAWB #", locationsById = {}, onClose }) {
  if (!stop) return null;
  const typeLabel = { pickup: "Pickup", delivery: "Delivery" }[stop.type] || "Stop";
  return (
    <div className="g-modal-overlay" onClick={onClose} style={{ alignItems: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", width: "100%", maxHeight: "85vh", overflowY: "auto",
        borderRadius: "18px 18px 0 0", boxSizing: "border-box",
        padding: "10px 16px 18px", paddingBottom: "calc(18px + env(safe-area-inset-bottom, 0px))",
      }}>
        <div style={{ width: 40, height: 5, background: "#d6dbe1", borderRadius: 3, margin: "0 auto 12px" }} />
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
          {number != null && (
            <div style={{
              width: 28, height: 28, borderRadius: "50%", background: "#185FA5", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0,
            }}>{number}</div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#111" }}>
              {stop.outOfTown ? "🚗 OUT OF TOWN" : (stop.locationName || "Stop")}
            </div>
            <div style={{ fontSize: 12, color: "#6b7684" }}>
              {typeLabel}{stop.scheduledTime ? ` · ${stop.scheduledTime}` : ""}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 24, color: "#888", padding: "0 4px", cursor: "pointer" }}>✕</button>
        </div>
        {stop.address && (
          <div style={{ marginBottom: 12 }}>
            <AddressLink stop={stop} style={{ fontSize: 14, marginBottom: 8 }} />
            <AddressLink stop={stop} prominent style={{ fontSize: 14, padding: "10px 14px" }} />
          </div>
        )}
        <StopContactsCard stop={stop} locationsById={locationsById} />
        {stop.accessNotes && (
          <div style={{ background: "#FFF8E6", border: "1px solid #EF9F27", borderRadius: 7, padding: "7px 10px", fontSize: 12, marginBottom: 10 }}>
            📌 {stop.accessNotes}
          </div>
        )}
        <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7684", letterSpacing: 0.5, textTransform: "uppercase", margin: "4px 0 6px" }}>
          Packages ({packages.length})
        </div>
        {packages.length === 0 && <div style={{ fontSize: 13, color: "#999", marginBottom: 6 }}>None listed yet.</div>}
        {packages.map((p, i) => (
          <div key={p.id || i} style={{ border: "1px solid #e3e6ea", borderRadius: 10, padding: "10px 12px", marginBottom: 6 }}>
            {/* Full number — never shortened (standing rule). */}
            <span style={{ fontSize: 17, fontWeight: 700, color: "#111", wordBreak: "break-all" }}>{p.hawbNumber || "(no number yet)"}</span>
            <span style={{ fontSize: 12, color: "#6b7684" }}> · {numLabel}</span>
            {p.podPhotoUrl && <span style={{ fontSize: 12, color: "#2d7a2d", fontWeight: 700 }}> · ✓ photo</span>}
          </div>
        ))}
        <div style={{ marginTop: 12, background: "#f2f4f7", borderRadius: 10, padding: 12, fontSize: 13, color: "#5b6675", textAlign: "center" }}>
          🔒 Start the job to add packages, take POD photos or skip.
        </div>
      </div>
    </div>
  );
}

export default function JobExecution() {
  const { jobId } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // Locations for this company, fetched once when the job loads. Used
  // to look up saved contacts for each stop. Indexed by id for fast
  // render-time lookup.
  const [locationsById, setLocationsById] = useState({});
  const [reorderMode, setReorderMode] = useState(false);
  // Stop opened as a view-only card from the start screen: { stop, number }.
  const [preview, setPreview] = useState(null);
  // Name of the next stop still to work, read from the rendered list (it is
  // in the same order the guard sees) for the pinned bottom bar.
  const [nextStopName, setNextStopName] = useState(null);
  useEffect(() => {
    const el = document.querySelector('[data-pending-stop="1"]');
    const name = el ? el.getAttribute("data-stop-name") : null;
    if (name !== nextStopName) setNextStopName(name);
  }, [job, nextStopName]);

  useEffect(() => {
    const unsub = watchJob(jobId, (data) => {
      setJob(data);
      setLoading(false);
    });
    return unsub;
  }, [jobId]);

  // Load this company's locations directory once we know which company.
  // Loaded read-only — guards see contacts but can't edit them.
  useEffect(() => {
    if (!userProfile?.companyId) return;
    getDocs(query(
      collection(db, "locations"),
      where("companyId", "==", userProfile.companyId),
    )).then((snap) => {
      const m = {};
      snap.docs.forEach((d) => { m[d.id] = { id: d.id, ...d.data() }; });
      setLocationsById(m);
    }).catch(() => {});
  }, [userProfile?.companyId]);

  async function handleStart() {
    await startJob(jobId);
  }

  async function handleRelease() {
    setReleasing(true);
    await guardReleaseJob(jobId, userProfile?.id);
    navigate("/guard/pool");
  }

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#aaa" }}>Loading job...</div>;
  if (!job) return <div style={{ padding: 32, textAlign: "center", color: "#aaa" }}>Job not found.</div>;

  const myAssignment = job.guardAssignments?.find((g) => g.guardId === userProfile?.id);
  const guardNumber = myAssignment?.guardNumber || 1;
  const stops = job.stops || [];
  const doneCount = stops.filter((s) => s.completedAt || s.noShow).length;
  // Display count excludes the airport — every airport job has one
  // implicitly, so saying "1 of 1 stops" reads more naturally than
  // "1 of 2 stops" for a single-pickup outbound. The data layer still
  // tracks the airport for execution flow and billing.
  const realStops = stops.filter((s) => s.type !== "airport");
  const realDoneCount = realStops.filter((s) => s.completedAt || s.noShow).length;

  // Rep info
  const repName = job.repName;
  const repPhone = job.repPhone;
  const scheduledTime = job.scheduledTime;
  // Time shown in the header — the job's time, or its earliest stop time.
  const headerTime = scheduledTime || stops.map((s) => s.scheduledTime).filter(Boolean).sort()[0] || "";

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* Job header */}
      <div style={{ background: "#0B1C2E", padding: "14px 16px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {/* Back to My Jobs WITHOUT touching the job — before this,
                the only exits were finishing or releasing to pool, so a
                guard who opened the wrong job had no safe way out. */}
            <button
              onClick={() => navigate("/guard/jobs")}
              aria-label="Back to My Jobs"
              style={{
                background: "rgba(255,255,255,0.14)", border: "none", color: "#fff",
                borderRadius: 8, width: 38, height: 38, fontSize: 18, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                padding: 0,
              }}
            >←</button>
            <span style={{
              fontWeight: 700, fontSize: 16, color: "#fff",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{jobDisplayNumber(job)}</span>
          </div>
          <span style={{
            background: "#fff", color: "#0B1C2E",
            borderRadius: 6, padding: "2px 9px", fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}>G{guardNumber}</span>
        </div>
        <div style={{ fontSize: 12, color: "#8ab4d4", marginBottom: 8 }}>
          {[job.brokerName, job.dispatcherName, headerTime].filter(Boolean).join(" · ")}
        </div>
        {/* Progress bar only on airport jobs. Directs are A→B with no
            useful "X of Y" framing — the guard just runs the package. */}
        {job.jobType !== "direct" && realStops.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.2)", borderRadius: 2, overflow: "hidden" }}>
              <div style={{ width: `${(realDoneCount / realStops.length) * 100}%`, height: "100%", background: "#4CAF96", borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 11, color: "#8ab4d4" }}>{realDoneCount} of {realStops.length} stop{realStops.length === 1 ? "" : "s"}</span>
          </div>
        )}
      </div>

      {/* Rep info */}
      {repName && (
        <div style={{ background: "#fff", padding: "10px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{repName}</div>
          </div>
          {repPhone && (
            <a
              href={`sms:${repPhone}?body=Hi ${repName || ""}%2C this is your courier confirming for job ${job.jobNumber}. On our way.`}
              style={{
                background: "#185FA5", color: "#fff", borderRadius: 8,
                padding: "8px 14px", fontSize: 12, fontWeight: 700,
                textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              💬 Text rep
            </a>
          )}
        </div>
      )}

      {/* Pre-start preview — show the origin (and destination preview) so the
          guard can navigate to the pickup BEFORE tapping Start. The Start
          button starts the billing clock, so we don't want guards tapping it
          while still in their driveway. */}
      {job.status === "confirmed" && (() => {
        // Order these the way the guard will actually work them, so "drive to
        // origin first" names the same stop the job opens on (array order is
        // dispatch order). Shared with My Jobs and the stop list's numbering.
        const workOrder = guardWorkOrder(job);
        const pkgsAt = (st) => (job.packages || []).filter((p) => p.stopId === st.id);
        const openPreview = (st, n) => setPreview({ stop: st, number: n });
        const origin = workOrder[0];
        const airportStop = (job.stops || []).find((s) => s.type === "airport");
        // Where the run ENDS. On an outbound that's the airport — naming the
        // last pickup as the destination read as "these are the only two
        // stops" on a five-pickup run.
        const destination = job.jobType === "outbound"
          ? (airportStop || null)
          : (workOrder.length > 1 ? workOrder[workOrder.length - 1] : null);
        // Everything between the origin and the end, so the count on this card
        // matches the "0 of N stops" in the header. Drop the destination when
        // it's one of these stops (inbound/direct) so it isn't listed twice —
        // on an outbound the destination is the airport and never is.
        const middle = workOrder.slice(1).filter((s) => s !== destination);
        return (
          <div style={{ padding: "14px 16px" }}>
            {origin && (
              <div style={{
                background: "#fff", border: "1px solid #e0e6f0", borderRadius: 10,
                padding: "12px 14px", marginBottom: 10,
              }}>
                <div style={{ fontSize: 10, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
                  Drive to origin first
                </div>
                <div onClick={() => openPreview(origin, 1)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 2 }}>
                  <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#185FA5", color: "#fff", fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>1</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#111", flex: 1 }}>{origin.locationName || "Origin"}</span>
                  <span style={{ fontSize: 12, color: "#185FA5", fontWeight: 700, whiteSpace: "nowrap" }}>Details ›</span>
                </div>
                {origin.address && (
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>{origin.address}</div>
                )}
                <AddressLink stop={origin} prominent />
              </div>
            )}
            {/* The remaining stops, in the order they'll be worked. Without
                this the card jumped straight from the origin to the end and a
                five-pickup run looked like a two-stop one. */}
            {middle.length > 0 && (
              <div style={{
                background: "#fff", border: "1px solid #e0e6f0", borderRadius: 10,
                padding: "10px 14px", marginBottom: 10,
              }}>
                <div style={{ fontSize: 10, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
                  Then {middle.length} more stop{middle.length !== 1 ? "s" : ""}
                </div>
                {middle.map((s, i) => (
                  <div key={s.id || i} onClick={() => openPreview(s, workOrder.indexOf(s) + 1)}
                    style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0", cursor: "pointer" }}>
                    <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#185FA5", color: "#fff", fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{workOrder.indexOf(s) + 1}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>
                        {s.outOfTown ? "🚗 OUT OF TOWN" : (s.locationName || "Stop")}
                      </div>
                      <div style={{ fontSize: 11, color: "#888" }}>
                        {[s.scheduledTime, pkgsAt(s).length > 0 && `${pkgsAt(s).length} package${pkgsAt(s).length === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <span style={{ color: "#98a4b3", fontSize: 14 }}>›</span>
                  </div>
                ))}
              </div>
            )}
            {destination && destination !== origin && (
              <div style={{
                background: "#f8fafc", border: "1px dashed #c0c8d4", borderRadius: 10,
                padding: "10px 14px", marginBottom: 10,
              }}>
                <div style={{ fontSize: 10, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
                  {job.jobType === "outbound" ? "Drop off at" : "Then heading to"}
                </div>
                {destination.outOfTown ? (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#a36b1d" }}>
                      🚗 OUT OF TOWN
                    </div>
                    <div style={{ fontSize: 11, color: "#7a5400", marginTop: 4 }}>
                      Follow the rep {
                        job.ootFollowMinutes ? `~${job.ootFollowMinutes} minutes` :
                        job.ootFollowMiles   ? `~${job.ootFollowMiles} miles` :
                        "for the broker-specified time"
                      } after they leave the origin, then break off.
                      No fixed destination address.
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111", marginBottom: 2 }}>
                      🏁 {destination.locationName || "Destination"}
                    </div>
                    {destination.address && (
                      <div style={{ fontSize: 11, color: "#666" }}>{destination.address}</div>
                    )}
                  </>
                )}
              </div>
            )}
            <button className="g-btn g-btn-green" onClick={handleStart} style={{ fontSize: 15 }}>
              ▶ Start job
            </button>
            <div style={{ fontSize: 10, color: "#888", textAlign: "center", marginTop: 6 }}>
              Tap once you arrive at the origin — this starts the billing clock.
            </div>
          </div>
        );
      })()}

      {/* Job type specific execution */}
      {job.status === "inProgress" && (
        <ReorderModeContext.Provider value={reorderMode}>
        <PhotoMetaContext.Provider value={{ companyId: job.companyId, jobNumber: job.jobNumber, guardName: userProfile?.name }}>
          {job.jobType === "outbound" && (
            <OutboundExecution job={job} guardNumber={guardNumber} userProfile={userProfile} locationsById={locationsById} />
          )}
          {job.jobType === "inbound" && (
            <InboundExecution job={job} guardNumber={guardNumber} userProfile={userProfile} locationsById={locationsById} />
          )}
          {job.jobType === "roundtrip" && (
            <RoundtripExecution job={job} guardNumber={guardNumber} userProfile={userProfile} locationsById={locationsById} />
          )}
          {job.jobType === "direct" && (
            <DirectExecution job={job} guardNumber={guardNumber} userProfile={userProfile} locationsById={locationsById} />
          )}
        </PhotoMetaContext.Provider>
        </ReorderModeContext.Provider>
      )}

      {/* Reordering is rare, so the ↑ ↓ arrows hide behind this switch. */}
      {job.status === "inProgress" && job.jobType !== "direct"
        && realStops.filter((s) => !s.completedAt && !s.noShow).length > 1 && (
        <div style={{ padding: "4px 12px 0" }}>
          <button className="g-btn g-btn-ghost" onClick={() => setReorderMode((on) => !on)}>
            {reorderMode ? "✓ Done reordering" : "↕ Reorder stops"}
          </button>
        </div>
      )}

      {/* Job complete — final acknowledgement screen with a Done button so
          the guard has a clear exit. completeJob fires automatically when the
          last stop is marked done; this surfaces the result and lets them
          navigate away. */}
      {job.status === "complete" && (
        <div style={{ padding: "24px 16px", display: "flex", flexDirection: "column", gap: 14, alignItems: "center", textAlign: "center" }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%", background: "#0E7C46",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 36, fontWeight: 700,
          }}>✓</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111" }}>
            Job complete
          </div>
          <div style={{ fontSize: 13, color: "#555", maxWidth: 320 }}>
            {job.jobNumber || job.brokerName} is closed out. The owner has been notified
            and will handle invoicing.
          </div>
          <button
            className="g-btn g-btn-primary"
            onClick={() => navigate("/guard/jobs")}
            style={{ marginTop: 6 }}
          >
            Done — back to my jobs
          </button>
        </div>
      )}

      {/* Release job */}
      {["confirmed","inProgress"].includes(job.status) && (
        <div style={{ padding: "16px 16px 0" }}>
          <button className="g-btn g-btn-danger" onClick={() => setShowReleaseModal(true)}>
            Release job back to pool
          </button>
        </div>
      )}

      {/* Pinned progress bar: how far along, and a jump to the next stop. */}
      {job.status === "inProgress" && job.jobType !== "direct" && realStops.length > 0 && (
        // keyed on its contents: iPhone Safari can skip repainting a sticky
        // bar's text, leaving "1 of 4 done" up after more stops finished.
        // A new element each time the contents change always paints.
        <div key={`${realDoneCount}|${nextStopName || ""}`} style={{
          position: "sticky", bottom: 0, zIndex: 5, marginTop: 12,
          background: "#fff", borderTop: "1px solid #e3e6ea",
          padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <span style={{ fontSize: 14, color: "#333", fontWeight: 600, whiteSpace: "nowrap" }}>
            {realDoneCount} of {realStops.length} done
          </span>
          {nextStopName && (
            <button
              onClick={() => document.querySelector('[data-pending-stop="1"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
              style={{ background: "none", border: "none", color: "#185FA5", fontSize: 14, fontWeight: 800, cursor: "pointer", textAlign: "right", minHeight: 40 }}
            >
              Next: {nextStopName} ›
            </button>
          )}
        </div>
      )}

      {preview && (
        <StopPreviewSheet
          stop={preview.stop}
          number={preview.number}
          packages={(job.packages || []).filter((p) => p.stopId === preview.stop.id)}
          numLabel={hawbNumLabel(job)}
          locationsById={locationsById}
          onClose={() => setPreview(null)}
        />
      )}

      {/* Release confirmation modal */}
      {showReleaseModal && (
        <div className="g-modal-overlay" onClick={() => setShowReleaseModal(false)}>
          <div className="g-modal" onClick={(e) => e.stopPropagation()}>
            <div className="g-modal-title">Release this job?</div>
            <div className="g-modal-body">
              The owner will be notified immediately that you've released {job.jobNumber} back to the pool. Only do this if you can no longer complete this job.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button className="g-btn g-btn-danger" onClick={handleRelease} disabled={releasing}>
                {releasing ? "Releasing..." : "Yes, release job"}
              </button>
              <button className="g-btn g-btn-ghost" onClick={() => setShowReleaseModal(false)}>
                Cancel — keep job
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── OUTBOUND EXECUTION ─────────────────────────────────────
// Pickups (all stops except the last) are open in parallel — the guard picks
// them up in whatever order traffic allows. The last stop (airport dropoff)
// stays locked until every pickup is complete.
function OutboundExecution({ job, guardNumber, userProfile, locationsById = {} }) {
  const stops = job.stops || [];
  const pickupCount = Math.max(0, stops.length - 1);
  const pickups = stops.slice(0, pickupCount);
  const airportStop = stops[stops.length - 1];
  // Each guard sees only the stops they cover (default: all). myPickups keeps
  // each pickup's TRUE index in `stops` so completion/reorder still target the
  // right row. The airport unlocks once THIS guard's covered pickups are done.
  //
  // Order by scheduled time, which is the order the run is actually worked —
  // the array is dispatch order and can put a 15:30 pickup above a 10:00 one.
  // The job card already sorts this way, so array order here meant the card
  // and the job itself disagreed about where to start. Untimed stops keep
  // their array position at the end. DISPLAY ONLY: `idx` stays the true index,
  // so completion, POD, billing and mileage legs are untouched.
  //
  // Once a guard reorders by hand, their order wins from then on — otherwise
  // the time sort would immediately undo the move and the arrows would look
  // broken.
  const sortByTime = !job.stopsManuallyOrdered;
  const myPickups = pickups
    .map((stop, idx) => ({ stop, idx }))
    .filter(({ stop }) => stopCoveredByGuard(stop, guardNumber))
    .sort((a, b) => {
      if (!sortByTime) return a.idx - b.idx;
      const ta = a.stop.scheduledTime || "";
      const tb = b.stop.scheduledTime || "";
      if (ta && tb) return ta.localeCompare(tb) || a.idx - b.idx;
      if (ta) return -1;
      if (tb) return 1;
      return a.idx - b.idx;
    });
  const airportCovered = stopCoveredByGuard(airportStop, guardNumber);
  const pickupsDone = myPickups.every(({ stop }) => stop.completedAt || stop.noShow);
  const [showAddPickup, setShowAddPickup] = useState(false);
  // Accordion over pickups — same pattern as InboundExecution's deliveries:
  // explicit tap wins while that pickup is open; otherwise auto-open the
  // first pickup still needing work. "__none__" = deliberately all closed.
  const [openPickupKey, setOpenPickupKey] = useState(null);
  const pickupKeyOf = (stop, idx) => stop.id || `p${idx}`;
  const firstOpenPickup = myPickups.find(({ stop }) => !stop.completedAt && !stop.noShow);
  const tappedPickupStillOpen = myPickups.some(({ stop, idx }) =>
    pickupKeyOf(stop, idx) === openPickupKey && !stop.completedAt && !stop.noShow);
  const effectiveOpenPickupKey = openPickupKey === "__none__"
    ? null
    : (openPickupKey != null && tappedPickupStillOpen)
      ? openPickupKey
      : (firstOpenPickup ? pickupKeyOf(firstOpenPickup.stop, firstOpenPickup.idx) : null);
  const airportDone = !!airportStop?.completedAt;

  // Safety net: if every stop is done but the job didn't flip to complete, do
  // it now. The inline check in handleCompleteStop can miss when two stops are
  // completed back-to-back (it runs on the array it built, which may pre-date
  // the other completion that just landed from the live subscription). This
  // also retroactively closes any job already stuck in that state.
  const autoCompletedRef = useRef(false);
  useEffect(() => {
    if (job.status !== "inProgress" || autoCompletedRef.current) return;
    const ss = job.stops || [];
    if (ss.length > 0 && ss.every((s) => s.completedAt || s.noShow)) {
      autoCompletedRef.current = true;
      completeJob(job.id, {}).catch((err) => {
        autoCompletedRef.current = false;
        console.warn("Outbound auto-complete failed", err);
      });
    }
  }, [job.stops, job.status, job.id]);

  // Swap two pickups by their TRUE indices in `stops`. The visible list may be
  // filtered to this guard's covered stops, so "up"/"down" pass the adjacent
  // VISIBLE pickup's real index rather than idx±1.
  async function handleSwapStops(a, b) {
    if (a < 0 || b < 0 || a >= pickupCount || b >= pickupCount) return;
    const newStops = [...stops];
    if (newStops[a].completedAt || newStops[b].completedAt) return;
    [newStops[a], newStops[b]] = [newStops[b], newStops[a]];
    // Stamp that a human set this order, so the scheduled-time sort stops
    // fighting the arrows and re-sorting the move away.
    await updateJob(job.id, { stops: newStops, stopsManuallyOrdered: true });
  }

  async function handleCompleteStop(idx, photoUrl, note) {
    const newStops = stops.map((s, i) =>
      i === idx
        ? {
            ...s,
            completedAt: new Date().toISOString(),
            // Record WHICH guard actually did this stop — the source of truth
            // for per-guard billing (the field plan can differ from dispatch).
            completedBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "", guardNumber },
            podPhotoUrl: photoUrl ?? null,
            note: note || s.note || "",
          }
        : s
    );
    await updateJobStops(job.id, newStops);
    if (newStops.every((s) => s.completedAt || s.noShow)) {
      await completeJob(job.id, {});
    }
  }

  async function handleAddNote(idx, note) {
    const newStops = stops.map((s, i) => i === idx ? { ...s, note: note || "" } : s);
    await updateJobStops(job.id, newStops);
  }

  async function handleSkipStop(idx, reason) {
    const now = new Date().toISOString();
    const newStops = stops.map((s, i) =>
      i === idx
        ? {
            ...s,
            noShow: true,
            noShowReason: reason,
            noShowAt: now,
            noShowBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "" },
          }
        : s
    );
    await updateJobStops(job.id, newStops);
    if (newStops.every((s) => s.completedAt || s.noShow)) {
      await completeJob(job.id, {});
    }
  }

  async function handleAddPickup({ locationName, address, lat, lng, accessNotes }) {
    // Generate an id so packages added at this stop can reference it via stopId,
    // and so the Malca workbook's HAWB lookup matches them to this row.
    const stopId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `stop_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const newStop = {
      id: stopId,
      type: "pickup",
      locationName: locationName || "",
      address: address || "",
      lat: lat ?? null,
      lng: lng ?? null,
      accessNotes: accessNotes || "",
      note: "",
      podPhotoUrl: null,
      addedInField: true,
      addedInFieldBy: {
        guardId: userProfile?.id || "",
        guardName: userProfile?.name || "",
      },
      addedInFieldAt: new Date().toISOString(),
    };
    // Insert before the airport (which is always last on outbound)
    const insertAt = Math.max(0, stops.length - 1);
    const newStops = [...stops.slice(0, insertAt), newStop, ...stops.slice(insertAt)];
    await updateJobStops(job.id, newStops);
  }

  return (
    <div>
      {/* Phase stepper — display only, derived from existing state. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 16px 4px" }}>
        {[
          { label: "Pickups", num: 1, done: pickupsDone },
          { label: "Airport", num: 2, done: airportDone },
          { label: "Done", num: 3, done: job.status === "complete" },
        ].map((ph, i, arr) => {
          const phActive = !ph.done && arr.slice(0, i).every((p) => p.done);
          return (
            <React.Fragment key={ph.label}>
              {i > 0 && (
                <div style={{ flex: 1, height: 2, borderRadius: 1, background: arr[i - 1].done ? "#2d7a2d" : "#e3e8ef" }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  background: ph.done ? "#2d7a2d" : phActive ? "#185FA5" : "#fff",
                  border: ph.done || phActive ? "none" : "2px solid #d5dce5",
                  color: "#fff", fontSize: 11, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{ph.done ? "✓" : ph.num}</div>
                <span style={{ fontSize: 12, fontWeight: phActive ? 800 : 600, color: ph.done ? "#2d7a2d" : phActive ? "#0B1C2E" : "#98a4b3" }}>
                  {ph.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {myPickups.length > 0 && (
        <div className="g-section-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <span>Pickups {pickupsDone ? "— all complete" : "— tap any in any order"}</span>
          <span style={{ color: "#a36b1d" }}>
            {myPickups.filter(({ stop }) => stop.completedAt || stop.noShow).length} of {myPickups.length} done
          </span>
        </div>
      )}
      {myPickups.map(({ stop, idx }, vIdx) => {
        const isDone = !!stop.completedAt;
        const prevVisible = myPickups[vIdx - 1];
        const nextVisible = myPickups[vIdx + 1];
        return (
          <StopCard
            key={stop.id || idx}
            stop={stop}
            idx={idx}
            isDone={isDone}
            isActive={!isDone}       // every incomplete pickup is active/clickable
            isLocked={false}
            // Accordion (presentation only) — same contract as inbound.
            collapsible={!isDone && !stop.noShow}
            expandedView={effectiveOpenPickupKey === pickupKeyOf(stop, idx)}
            onToggleExpand={() => {
              const k = pickupKeyOf(stop, idx);
              setOpenPickupKey(effectiveOpenPickupKey === k ? "__none__" : k);
            }}
            // Any guard assigned to a stop can shoot its POD — not just G1/G2.
            // Legacy jobs (no per-stop coverage) keep the old lead/passenger rule.
            requiresPhoto={(stop.guardNumbers && stop.guardNumbers.length) ? true : (guardNumber === 1 || guardNumber === 2)}
            // Reorder any non-completed pickup
            showReorder={!isDone}
            canMoveUp={!!prevVisible && !prevVisible.stop.completedAt}
            canMoveDown={!!nextVisible && !nextVisible.stop.completedAt}
            onMoveUp={() => prevVisible && handleSwapStops(idx, prevVisible.idx)}
            onMoveDown={() => nextVisible && handleSwapStops(idx, nextVisible.idx)}
            onComplete={(photoUrl, note) => handleCompleteStop(idx, photoUrl, note)}
            onAddNote={(note) => handleAddNote(idx, note)}
            onSkip={(reason) => handleSkipStop(idx, reason)}
            jobId={job.id}
            guardId={userProfile?.id}
            label={`Pickup ${vIdx + 1}`}
            displayNumber={vIdx + 1}
            locationsById={locationsById}
            hawbLabel={hawbLabel(job)}
            // Per-package POD + HAWB capture at each pickup. Guard types
            // the HAWB from the POD form, photographs the form. Handlers
            // mirror those on StopCard.
            stopPackages={(job.packages || []).filter((p) => p.stopId === stop.id)}
            onPackagePhoto={async (pkgId, url) => {
              const newPackages = (job.packages || []).map((p) =>
                p.id === pkgId ? { ...p, podPhotoUrl: url } : p
              );
              await updateJob(job.id, { packages: newPackages });
            }}
            onAddPackage={async (stopId) => {
              const newPkg = {
                id: `pkg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                stopId, hawbNumber: "", podPhotoUrl: null,
                addedByGuard: true, addedAt: new Date().toISOString(),
              };
              await updateJob(job.id, { packages: [...(job.packages || []), newPkg] });
            }}
            onUpdatePackageHawb={async (pkgId, hawb) => {
              const newPackages = (job.packages || []).map((p) =>
                p.id === pkgId ? { ...p, hawbNumber: hawb } : p
              );
              await updateJob(job.id, { packages: newPackages });
            }}
            onRemovePackage={async (pkgId) => {
              const newPackages = (job.packages || []).filter((p) => p.id !== pkgId);
              await updateJob(job.id, { packages: newPackages });
            }}
            onPackageSkip={async (pkgId, reason) => {
              const now = new Date().toISOString();
              const newPackages = (job.packages || []).map((p) =>
                p.id === pkgId
                  ? {
                      ...p,
                      scheduledInError: true,
                      scheduledInErrorReason: reason,
                      scheduledInErrorAt: now,
                      scheduledInErrorBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "" },
                    }
                  : p
              );
              await updateJob(job.id, { packages: newPackages });
            }}
            onUndoPackageSkip={async (pkgId) => {
              const newPackages = (job.packages || []).map((p) =>
                p.id === pkgId
                  ? { ...p, scheduledInError: false, scheduledInErrorReason: null, scheduledInErrorAt: null, scheduledInErrorBy: null }
                  : p
              );
              await updateJob(job.id, { packages: newPackages });
            }}
          />
        );
      })}

      {/* Guard can add a pickup in the field (e.g. last-minute call from a
          customer), or tack a forgotten package onto a stop that's already
          done. Stays visible until the airport drop is complete. */}
      {!airportStop?.completedAt && (
        <div style={{ padding: "4px 12px 8px" }}>
          <button
            className="g-btn g-btn-ghost"
            onClick={() => setShowAddPickup(true)}
            style={{ borderStyle: "dashed", borderWidth: 1, borderColor: "#185FA5", color: "#185FA5" }}
          >
            ➕ Add pickup or forgotten package
          </button>
        </div>
      )}

      {showAddPickup && (
        <AddPickupModal
          onClose={() => setShowAddPickup(false)}
          onAdd={async (data) => {
            await handleAddPickup(data);
            setShowAddPickup(false);
          }}
          existingStops={pickups}
          existingLocations={locationsById}
          jobId={job.id}
          guardId={userProfile?.id}
          hawbNum={hawbNumLabel(job)}
          onAddPackageToExistingStop={async ({ stopId, hawbNumber, podPhotoUrl }) => {
            const stop = stops.find((s) => s.id === stopId);
            const newPkg = {
              id: `pkg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              stopId,
              hawbNumber: hawbNumber || "",
              podPhotoUrl: podPhotoUrl || null,
              pickupTime: stop?.completedAt || new Date().toISOString(),
              addedByGuard: true,
              addedLate: !!stop?.completedAt,
              addedAt: new Date().toISOString(),
              addedBy: {
                guardId: userProfile?.id || "",
                guardName: userProfile?.name || "",
              },
            };
            await updateJob(job.id, { packages: [...(job.packages || []), newPkg] });
            setShowAddPickup(false);
          }}
        />
      )}

      {/* Airport dropoff — locked until every pickup is done.
          No POD photo required at the airport; once all pickups are complete
          the guard gets a "Is the job complete?" confirmation. Hidden for a
          guard who doesn't cover the airport (e.g. peeled off after a pickup). */}
      {airportStop && airportCovered && (
        <>
          <div className="g-section-label">
            Airport dropoff {airportStop.completedAt ? "— done" : pickupsDone ? "— ready" : "— unlocks after all pickups"}
          </div>

          {/* Locked state (pickups not yet done) */}
          {!pickupsDone && !airportStop.completedAt && (
            <div className="g-stop g-stop-locked" style={{ margin: "0 12px 6px", borderRadius: 12, padding: "12px 14px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🛫 {airportStop.locationName || "Airport dropoff"}</div>
              {airportStop.address && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{airportStop.address}</div>}
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>Finish all pickups first.</div>
            </div>
          )}

          {/* Ready — show the "Is the job complete?" confirmation */}
          {pickupsDone && !airportStop.completedAt && (
            <div className="g-card" style={{ margin: "8px 12px", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
              <div style={{ padding: "14px" }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                  🛫 {airportStop.locationName || "Airport dropoff"}
                </div>
                {airportStop.address && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                      airportStop.lat && airportStop.lng ? `${airportStop.lat},${airportStop.lng}` : airportStop.address
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 12, color: "#185FA5", display: "inline-block", textDecoration: "underline", textDecorationColor: "#b9d0e6", marginBottom: 10 }}
                  >
                    {airportStop.address}  🧭
                  </a>
                )}
                <div style={{ background: "#E6F7EC", border: "1px solid #b3d9be", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#1a4a1a", marginBottom: 12 }}>
                  All pickups are complete. Drop off at the airport, then confirm below.
                </div>
                <button
                  className="g-btn g-btn-green"
                  onClick={async () => {
                    if (!window.confirm("Is this job complete?")) return;
                    await handleCompleteStop(stops.length - 1, null, "");
                  }}
                  style={{ fontSize: 15 }}
                >
                  ✓ Yes — job complete
                </button>
              </div>
            </div>
          )}

          {/* Done state */}
          {airportStop.completedAt && (
            <div className="g-stop g-stop-done" style={{ margin: "0 12px 6px", borderRadius: 12, padding: "12px 14px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🛫 {airportStop.locationName || "Airport dropoff"}</div>
              <div style={{ fontSize: 11, color: "#2d7a2d", marginTop: 4 }}>
                ✓ Completed {new Date(airportStop.completedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── INBOUND EXECUTION ──────────────────────────────────────
function InboundExecution({ job, guardNumber, userProfile, locationsById = {} }) {
  const stops = job.stops || [];
  const packages = job.packages || [];
  // Normally stops[0] IS the airport. A vault delivery is the exception: it
  // keeps jobType "inbound" but the pieces were already in hand at the house,
  // so there's no airport stop at all. Assuming stops[0] blindly there would
  // present the first DELIVERY as the airport ("was this received at the
  // airport?") and leave the route with zero deliveries to complete.
  const hasAirport = stops[0]?.type === "airport";
  const airportStop = hasAirport ? stops[0] : null;
  const deliveryStops = hasAirport ? stops.slice(1) : stops;
  // Index offset from deliveryStops back into the full `stops` array.
  const stopOffset = hasAirport ? 1 : 0;
  // No airport means nothing to collect — the deliveries are open immediately.
  const airportDone = hasAirport ? !!airportStop?.completedAt : true;
  // Each guard sees only the deliveries they cover (default: all). stopIdx is
  // the index into deliveryStops; + stopOffset reaches the full `stops`
  // array. All inbound guards collect at the airport, so it's not filtered —
  // only the deliveries diverge.
  // Order by scheduled time — the order the run is actually worked — so this
  // matches the job card and the pre-start summary. Untimed stops keep their
  // array position at the end. DISPLAY ONLY: stopIdx stays the true index, so
  // completion, POD, billing and mileage legs are untouched. A guard's manual
  // reorder wins from then on, else the sort would undo the arrows.
  const sortDeliveriesByTime = !job.stopsManuallyOrdered;
  const myDeliveries = deliveryStops
    .map((stop, i) => ({ stop, stopIdx: i }))
    .filter(({ stop }) => stopCoveredByGuard(stop, guardNumber))
    .sort((a, b) => {
      if (!sortDeliveriesByTime) return a.stopIdx - b.stopIdx;
      const ta = a.stop.scheduledTime || "";
      const tb = b.stop.scheduledTime || "";
      if (ta && tb) return ta.localeCompare(tb) || a.stopIdx - b.stopIdx;
      if (ta) return -1;
      if (tb) return 1;
      return a.stopIdx - b.stopIdx;
    });
  const [showScan, setShowScan] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Accordion: which delivery card is open to work. null = auto (the
  // first not-done delivery). Purely presentational — collapsed cards
  // stay mounted and just hide their body (display:none), so in-flight
  // photo uploads and retry state survive collapsing.
  const [openDeliveryKey, setOpenDeliveryKey] = useState(null);
  const deliveryKeyOf = (stop, stopIdx) => stop.id || `d${stopIdx}`;
  // Ferrari (and other simpler brokers) skip the seal-photo flow at
  // the airport — they don't deal in tamper-evident sealed bags. The
  // guard just confirms receipt and heads to the delivery. Malca-Amit
  // is the broker that requires the full seal/color/inner workflow.
  const brokerName = String(job.brokerName || "").toLowerCase();
  // A guard can be exempted from the seal-photo flow (sealPhotoExempt on their
  // profile) — they just confirm receipt like the simpler brokers. Temporary
  // per-guard override until seals are handled another way.
  const sealExempt = !!userProfile?.sealPhotoExempt;
  const requiresSealCheck = brokerName.includes("malca") && !sealExempt;

  // A package is "ready" depending on broker rules:
  //   Malca-style: outer seal photo + RED, OR outer seal + GREEN + inner.
  //   Ferrari-style: just confirmed received (no seal photos required).
  //   Either: marked not-on-flight is also "ready" (skip).
  const pkgReady = (pkg) => {
    if (pkg.notOnFlight) return true;
    if (!requiresSealCheck) {
      // Ferrari and similar — guard just acknowledges receipt.
      return !!pkg.receivedAt;
    }
    if (!pkg.photos?.outerSeal) return false;
    if (pkg.outerSealColor === "red")   return true;
    if (pkg.outerSealColor === "green") return !!pkg.photos?.innerSeal;
    return false; // outer photo taken but color not yet recorded
  };

  const arrivedPkgs   = packages.filter((p) => !p.notOnFlight);
  const missedPkgs    = packages.filter((p) =>  p.notOnFlight);
  const allPkgsReady  = packages.length > 0 && packages.every(pkgReady);
  const nothingArrived = packages.length > 0 && arrivedPkgs.length === 0;

  // Group the airport packages by where each is headed (its delivery
  // stop), so the guard photographing seals sees the destination, not
  // just a receipt number. Each item keeps its ORIGINAL package index so
  // the seal/photo handlers still target the right package. Groups follow
  // the delivery route order; anything unbound lands in "Unassigned".
  const packageGroups = (() => {
    const withIdx = packages.map((pkg, pkgIdx) => ({ pkg, pkgIdx }));
    const groups = [];
    const seen = new Set();
    deliveryStops.forEach((stop) => {
      seen.add(stop.id);
      const items = withIdx.filter(({ pkg }) => pkg.stopId === stop.id);
      if (items.length) groups.push({ id: stop.id, name: stop.locationName || stop.address || "Delivery", items });
    });
    const orphans = withIdx.filter(({ pkg }) => !pkg.stopId || !seen.has(pkg.stopId));
    if (orphans.length) groups.push({ id: "unassigned", name: "Unassigned", items: orphans });
    return groups;
  })();

  const user = { id: userProfile?.id, name: userProfile?.name, role: "guard" };

  async function handleMarkMissed(pkgIdx) {
    await markPackageNotOnFlight(job.id, pkgIdx, user, "");
  }
  async function handleUnmarkMissed(pkgIdx) {
    await unmarkPackageNotOnFlight(job.id, pkgIdx, user);
  }

  async function handleLeaveAirport() {
    if (!allPkgsReady) return;
    const newStops = stops.map((s, i) => i === 0
      ? { ...s, completedAt: new Date().toISOString(), completedBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "", guardNumber } }
      : s);
    await updateJobStops(job.id, newStops);
  }

  // Special path: every expected package missed the flight, nothing arrived.
  // Mark airport complete + no-show all remaining delivery stops + complete job
  // with a metadata flag for downstream reporting.
  async function handleCompleteNoPackages() {
    if (!window.confirm("Confirm: no packages arrived on the flight. This will complete the job.")) return;
    setCompleting(true);
    const now = new Date().toISOString();
    const newStops = stops.map((s, i) =>
      i === 0
        ? { ...s, completedAt: now, noPackagesReceived: true }
        : { ...s, noShow: true, noShowReason: "No packages arrived on flight", noShowAt: now }
    );
    await updateJobStops(job.id, newStops);
    await completeJob(job.id, {
      noPackagesReceived: true,
      airportOutcome:     "all_missed_flight",
      completedByGuardId: userProfile?.id,
    });
    setCompleting(false);
  }

  // Simple "received this package" toggle for Ferrari-style brokers.
  // Sets receivedAt timestamp; pkgReady() then treats the package as
  // ready without any seal photos. Toggling off removes the marker.
  async function handlePackageReceived(pkgIdx, received) {
    const newPackages = packages.map((pkg, i) =>
      i === pkgIdx ? {
        ...pkg,
        receivedAt: received ? new Date().toISOString() : null,
        receivedBy: received
          ? { guardId: userProfile?.id || "", guardName: userProfile?.name || "" }
          : null,
      } : pkg
    );
    await updateJob(job.id, { packages: newPackages });
  }

  async function handlePackagePhoto(pkgIdx, photoType, url) {
    const newPackages = packages.map((pkg, i) =>
      i === pkgIdx ? { ...pkg, photos: { ...pkg.photos, [photoType]: { url, takenAt: new Date().toISOString() } } } : pkg
    );
    await updateJob(job.id, { packages: newPackages });
  }

  async function handleSetSealColor(pkgIdx, color) {
    const newPackages = packages.map((pkg, i) =>
      i === pkgIdx ? { ...pkg, outerSealColor: color, sealColorSetAt: new Date().toISOString() } : pkg
    );
    await updateJob(job.id, { packages: newPackages });
  }

  // Delivery: mark the stop complete. Does NOT auto-complete the job —
  // the guard has to tap the explicit "Complete job" button once all deliveries are done.
  async function handleCompleteDelivery(stopIdx, photoUrl) {
    const absoluteIdx = stopIdx + stopOffset; // skip the airport stop when there is one
    const newStops = stops.map((s, i) =>
      i === absoluteIdx
        ? {
            ...s,
            completedAt: new Date().toISOString(),
            // Record which guard delivered this stop — source of truth for billing.
            completedBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "", guardNumber },
            podPhotoUrl: photoUrl ?? null,
          }
        : s
    );
    await updateJobStops(job.id, newStops);
  }

  const deliveriesDone = deliveryStops.length > 0 && deliveryStops.every((s) => s.completedAt || s.noShow);
  const canCompleteJob = airportDone && deliveriesDone;

  async function handleCompleteJob() {
    if (!canCompleteJob) return;
    setCompleting(true);
    await completeJob(job.id, {
      completedByGuardId: userProfile?.id,
      completedByGuardName: userProfile?.name,
    });
    setCompleting(false);
  }

  // Which delivery card is open: the guard's explicit tap wins while that
  // stop still needs work; once it's completed/skipped (or before any tap),
  // auto-advance to the first delivery that still needs work. "__none__"
  // (guard collapsed everything on purpose) stays collapsed.
  const firstOpenDelivery = myDeliveries.find(({ stop }) => !stop.completedAt && !stop.noShow);
  const tappedStillOpen = myDeliveries.some(({ stop, stopIdx }) =>
    deliveryKeyOf(stop, stopIdx) === openDeliveryKey && !stop.completedAt && !stop.noShow);
  const effectiveOpenKey = openDeliveryKey === "__none__"
    ? null
    : (openDeliveryKey != null && tappedStillOpen)
      ? openDeliveryKey
      : (firstOpenDelivery ? deliveryKeyOf(firstOpenDelivery.stop, firstOpenDelivery.stopIdx) : null);
  const remainingMine = myDeliveries.filter(({ stop }) => !stop.completedAt && !stop.noShow).length;

  return (
    <div>
      {/* Phase stepper — display only, derived from existing state. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 16px 4px" }}>
        {[
          // No airport step on a vault delivery — it starts at the house.
          ...(hasAirport ? [{ label: "Airport", done: airportDone }] : []),
          { label: "Deliver", done: deliveriesDone },
          { label: "Done", done: job.status === "complete" },
        ].map((ph, i, arr) => {
          const num = i + 1;
          const phActive = !ph.done && arr.slice(0, i).every((p) => p.done);
          return (
            <React.Fragment key={ph.label}>
              {i > 0 && (
                <div style={{ flex: 1, height: 2, borderRadius: 1, background: arr[i - 1].done ? "#2d7a2d" : "#e3e8ef" }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  background: ph.done ? "#2d7a2d" : phActive ? "#185FA5" : "#fff",
                  border: ph.done || phActive ? "none" : "2px solid #d5dce5",
                  color: "#fff", fontSize: 11, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{ph.done ? "✓" : num}</div>
                <span style={{ fontSize: 12, fontWeight: phActive ? 800 : 600, color: ph.done ? "#2d7a2d" : phActive ? "#0B1C2E" : "#98a4b3" }}>
                  {ph.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Airport stop — absent on a vault delivery, which starts at the
          house with the pieces already in hand. */}
      {hasAirport && (<>
      <div className="g-section-label">Airport — collect all packages</div>
      <div className="g-card" style={{ margin: "8px 12px" }}>
        <div style={{ padding: "12px 14px" }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
            🛬 {airportStop?.locationName || "Airport"} {airportDone && <span style={{ color: "#2d7a2d", fontSize: 12 }}>✓ Done</span>}
          </div>
          {airportStop?.address && <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>{airportStop.address}</div>}
          {airportStop?.accessNotes && (
            <div style={{ background: "#FFF8E6", border: "1px solid #EF9F27", borderRadius: 7, padding: "8px 10px", fontSize: 11, marginBottom: 10 }}>
              📌 {airportStop.accessNotes}
            </div>
          )}

          {!airportDone && packages.length === 0 && (
            <div style={{ fontSize: 12, color: "#aaa", textAlign: "center", padding: "10px 0" }}>
              No packages assigned to this job
            </div>
          )}

          {/* Package workflow — grouped by destination so the guard knows
              which delivery each sealed bag is for, then outer seal first,
              then the color-driven next step. */}
          {!airportDone && packageGroups.map((group) => (
          <div key={group.id}>
            <div style={{
              fontSize: 11, fontWeight: 800, color: "#185FA5",
              textTransform: "uppercase", letterSpacing: 0.4, margin: "12px 2px 6px",
            }}>
              📍 {group.name} <span style={{ color: "#888", fontWeight: 600 }}>· {group.items.length} pkg</span>
            </div>
            {group.items.map(({ pkg, pkgIdx }) => {
            const hasOuter    = !!pkg.photos?.outerSeal;
            const hasInner    = !!pkg.photos?.innerSeal;
            const color       = pkg.outerSealColor; // "red" | "green" | undefined
            const ready       = pkgReady(pkg);
            const addedByGuard = pkg.source?.type === "guard_scan_airport";
            const missed      = !!pkg.notOnFlight;
            const noPhotosYet = !hasOuter && !hasInner;

            // Progress label varies by broker. Malca uses the seal flow;
            // Ferrari just needs an acknowledgement tap.
            let progress;
            if (!requiresSealCheck) {
              progress = pkg.receivedAt ? "✓ Received" : "Tap to confirm received";
            } else {
              progress = "Step 1 of 2 — photograph outer seal";
              if (hasOuter && !color)     progress = "Step 2 of 2 — what color was the outer seal?";
              if (color === "red")        progress = "✓ Outer seal RED — no inner check needed";
              if (color === "green" && !hasInner) progress = "Step 3 of 3 — photograph inner seal or bag number";
              if (color === "green" && hasInner)  progress = "✓ Outer GREEN + inner verified";
            }

            return (
              <div key={pkgIdx} className={`g-pkg${ready && !missed ? " g-pkg-complete" : ""}`}
                style={missed ? { opacity: 0.75, borderLeft: "3px solid #a32d2d" } : undefined}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {hawbLabel(job)}: {pkg.hawbNumber || `Package ${pkgIdx + 1}`}
                    </div>
                    {!missed && (
                      <div style={{ fontSize: 11, color: ready ? "#2d7a2d" : "#888", marginTop: 2 }}>
                        {progress}
                      </div>
                    )}
                    {addedByGuard && (
                      <div style={{ fontSize: 10, color: "#185FA5", fontWeight: 600, marginTop: 2 }}>
                        🙋 Added at airport by {pkg.source.addedByName || "guard"}
                      </div>
                    )}
                  </div>
                  {ready && !missed && <span style={{ color: "#2d7a2d", fontSize: 18 }}>✓</span>}
                </div>

                {/* Missed-flight state */}
                {missed ? (
                  <div style={{
                    background: "#fdecec", border: "1px solid #f5a5a5", borderRadius: 7,
                    padding: "8px 10px", fontSize: 12, color: "#a32d2d",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                  }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>❌ Not on flight</div>
                      <div style={{ fontSize: 10, color: "#a32d2d", opacity: 0.8 }}>
                        Marked by {pkg.notOnFlightBy?.userName || "guard"}
                      </div>
                    </div>
                    <button
                      className="g-btn g-btn-ghost g-btn-sm"
                      style={{ minHeight: 30, padding: "4px 10px", fontSize: 11 }}
                      onClick={() => handleUnmarkMissed(pkgIdx)}
                    >
                      Undo
                    </button>
                  </div>
                ) : !requiresSealCheck ? (
                  // Ferrari-style — no seal photos. Guard taps once to
                  // acknowledge receipt; tap again to toggle off in case
                  // of misclick.
                  <button
                    onClick={() => handlePackageReceived(pkgIdx, !pkg.receivedAt)}
                    className={`g-btn ${pkg.receivedAt ? "g-btn-green" : "g-btn-primary"}`}
                    style={{ width: "100%", marginBottom: 6 }}
                  >
                    {pkg.receivedAt ? "✓ Received — tap to undo" : "📦 Mark package received"}
                  </button>
                ) : (
                  <>
                    {/* Step 1: always take outer seal photo */}
                    <PhotoCapture
                      label="📦 Outer seal"
                      existing={pkg.photos?.outerSeal?.url}
                      onCapture={(url) => handlePackagePhoto(pkgIdx, "outerSeal", url)}
                      jobId={job.id}
                      stopId={`pkg_${pkgIdx}_outerSeal`}
                      guardId={userProfile?.id}
                      photoType="outerSeal"
                      style={{ marginBottom: 8 }}
                    />

                    {/* Step 2: after outer photo, choose color */}
                    {hasOuter && !color && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>
                          What color is the outer seal?
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => handleSetSealColor(pkgIdx, "red")}
                            style={{
                              flex: 1, background: "#a32d2d", color: "#fff",
                              border: "none", borderRadius: 8, padding: "10px 12px",
                              fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 44,
                            }}
                          >
                            🔴 Red
                          </button>
                          <button
                            onClick={() => handleSetSealColor(pkgIdx, "green")}
                            style={{
                              flex: 1, background: "#2d7a2d", color: "#fff",
                              border: "none", borderRadius: 8, padding: "10px 12px",
                              fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 44,
                            }}
                          >
                            🟢 Green
                          </button>
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 6, lineHeight: 1.4 }}>
                          Red = secure, no inner check. Green = open the bag and photograph the inner seal or bag number.
                        </div>
                      </div>
                    )}

                    {/* Step 3: if green, take inner-seal/bag-number photo */}
                    {color === "green" && (
                      <PhotoCapture
                        label="🔒 Inner seal or bag number"
                        existing={pkg.photos?.innerSeal?.url}
                        onCapture={(url) => handlePackagePhoto(pkgIdx, "innerSeal", url)}
                        jobId={job.id}
                        stopId={`pkg_${pkgIdx}_innerSeal`}
                        guardId={userProfile?.id}
                        photoType="innerSeal"
                        style={{ marginBottom: 6 }}
                      />
                    )}

                    {/* Color-change escape hatch — if the guard misclicked */}
                    {color && (
                      <button
                        className="g-btn g-btn-ghost g-btn-sm"
                        style={{ marginTop: 4, fontSize: 11, minHeight: 30 }}
                        onClick={() => handleSetSealColor(pkgIdx, null)}
                      >
                        Change seal color
                      </button>
                    )}

                    {/* Not-on-flight toggle — only before any photos taken */}
                    {noPhotosYet && (
                      <button
                        className="g-btn g-btn-ghost g-btn-sm"
                        style={{
                          marginTop: 4, fontSize: 11, color: "#a32d2d",
                          borderColor: "#e8a5a5", minHeight: 32,
                        }}
                        onClick={() => handleMarkMissed(pkgIdx)}
                      >
                        ❌ This package wasn't on the flight
                      </button>
                    )}
                  </>
                )}
              </div>
            );
            })}
          </div>
          ))}

          {/* Missed-flight summary */}
          {!airportDone && missedPkgs.length > 0 && (
            <div style={{
              background: "#fdecec", border: "1px solid #f5a5a5", borderRadius: 7,
              padding: "8px 10px", fontSize: 11, color: "#a32d2d", marginTop: 8,
            }}>
              ❌ {missedPkgs.length} package{missedPkgs.length !== 1 ? "s" : ""} not on flight
              {arrivedPkgs.length > 0 && ` · ${arrivedPkgs.length} to deliver`}
            </div>
          )}

          {/* Scan unexpected package */}
          {!airportDone && (
            <button
              className="g-btn g-btn-ghost"
              onClick={() => setShowScan(true)}
              style={{ marginTop: 8, borderStyle: "dashed", borderWidth: 1, borderColor: "#185FA5", color: "#185FA5" }}
            >
              ➕ Scan unexpected package
            </button>
          )}

          {/* Three-way leave/complete/disabled logic */}
          {!airportDone && packages.length === 0 && (
            <button className="g-btn g-btn-ghost" disabled style={{ marginTop: 8 }}>
              No packages yet — scan at least one to continue
            </button>
          )}

          {!airportDone && nothingArrived && (
            <button
              className="g-btn g-btn-danger"
              onClick={handleCompleteNoPackages}
              disabled={completing}
              style={{ marginTop: 8 }}
            >
              {completing ? "Completing..." : "🛬 No packages received — complete job"}
            </button>
          )}

          {!airportDone && !nothingArrived && packages.length > 0 && (
            <button
              className={`g-btn ${allPkgsReady ? "g-btn-green" : "g-btn-ghost"}`}
              onClick={handleLeaveAirport}
              disabled={!allPkgsReady}
              style={{ marginTop: 8 }}
            >
              {allPkgsReady
                ? `✓ Leave airport — ${arrivedPkgs.length} to deliver`
                : `Photograph or mark each package (${packages.filter(pkgReady).length}/${packages.length} ready)`}
            </button>
          )}
        </div>
      </div>
      </>)}

      {/* Scan modal */}
      {showScan && (
        <ScanPackageModal
          jobId={job.id}
          hawbLabel={hawbLabel(job)}
          user={{ id: userProfile?.id, name: userProfile?.name, role: "guard" }}
          onClose={() => setShowScan(false)}
          onAdded={() => setShowScan(false)}
        />
      )}

      {/* Delivery stops — unlock after airport */}
      {myDeliveries.length > 0 && (
        <>
          <div className="g-section-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
            <span>Deliveries {airportDone ? "— complete in any order" : "— unlocks after airport step"}</span>
            <span style={{ color: "#a36b1d" }}>
              {myDeliveries.filter(({ stop }) => stop.completedAt || stop.noShow).length} of {myDeliveries.length} done
            </span>
          </div>
          {myDeliveries.map(({ stop, stopIdx: idx }, vIdx) => {
            const isDone = !!stop.completedAt;
            // Real index into `stops`. stopOffset is 1 when an airport leads
            // the route and 0 on a vault delivery that never goes near one —
            // hardcoding 1 there pointed skip/note/reorder at the wrong stop.
            const realIdx = idx + stopOffset;
            const prevVisible = myDeliveries[vIdx - 1];
            const nextVisible = myDeliveries[vIdx + 1];
            return (
              <StopCard
                key={stop.id || idx}
                stop={stop}
                idx={idx}
                isDone={isDone}
                isActive={airportDone && !isDone}
                isLocked={!airportDone}
                requiresPhoto={true}
                // Accordion (presentation only): tap a delivery header to
                // open it; collapsed cards stay mounted with body hidden.
                collapsible={airportDone && !isDone && !stop.noShow}
                expandedView={effectiveOpenKey === deliveryKeyOf(stop, idx)}
                onToggleExpand={() => {
                  const k = deliveryKeyOf(stop, idx);
                  setOpenDeliveryKey(effectiveOpenKey === k ? "__none__" : k);
                }}
                // Allow reordering any remaining delivery (airport done, this one not done)
                showReorder={airportDone && !isDone}
                canMoveUp={!!prevVisible && !prevVisible.stop.completedAt}
                canMoveDown={!!nextVisible && !nextVisible.stop.completedAt}
                onMoveUp={async () => {
                  if (!prevVisible) return;
                  const newStops = [...stops];
                  const b = prevVisible.stopIdx + stopOffset;
                  [newStops[realIdx], newStops[b]] = [newStops[b], newStops[realIdx]];
                  await updateJob(job.id, { stops: newStops, stopsManuallyOrdered: true });
                }}
                onMoveDown={async () => {
                  if (!nextVisible) return;
                  const newStops = [...stops];
                  const b = nextVisible.stopIdx + stopOffset;
                  [newStops[realIdx], newStops[b]] = [newStops[b], newStops[realIdx]];
                  await updateJob(job.id, { stops: newStops, stopsManuallyOrdered: true });
                }}
                onComplete={(photoUrl) => handleCompleteDelivery(idx, photoUrl)}
                onAddNote={async (note) => {
                  const newStops = stops.map((s, i) => i === realIdx ? { ...s, note } : s);
                  await updateJobStops(job.id, newStops);
                }}
                onSkip={async (reason) => {
                  const now = new Date().toISOString();
                  const newStops = stops.map((s, i) =>
                    i === realIdx
                      ? {
                          ...s,
                          noShow: true,
                          noShowReason: reason,
                          noShowAt: now,
                          noShowBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "" },
                        }
                      : s
                  );
                  await updateJobStops(job.id, newStops);
                }}
                jobId={job.id}
                guardId={userProfile?.id}
                label={`Delivery ${vIdx + 1}`}
                displayNumber={vIdx + 1}
                locationsById={locationsById}
                hawbLabel={hawbLabel(job)}
                // Per-package POD + HAWB capture at each delivery. Guard
                // types the HAWB read off the POD form (the canonical
                // searchable number Malca/Ferrari call about) and
                // photographs the form. Handlers mirror Outbound.
                // Include packages with no stopId — e.g. an extra package the
                // guard scanned at the airport (it lands unassigned). They show
                // at the active delivery stop so the guard can photograph them,
                // and get bound to this stop on the first POD photo.
                stopPackages={packages.filter((p) => p.stopId === stop.id || !p.stopId)}
                onPackagePhoto={async (pkgId, url) => {
                  const newPackages = packages.map((p) =>
                    p.id === pkgId ? { ...p, podPhotoUrl: url, stopId: p.stopId || stop.id } : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onAddPackage={async (stopId) => {
                  const newPkg = {
                    id: `pkg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                    stopId, hawbNumber: "", podPhotoUrl: null,
                    addedByGuard: true, addedAt: new Date().toISOString(),
                  };
                  await updateJob(job.id, { packages: [...packages, newPkg] });
                }}
                onUpdatePackageHawb={async (pkgId, hawb) => {
                  const newPackages = packages.map((p) =>
                    p.id === pkgId ? { ...p, hawbNumber: hawb } : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onRemovePackage={async (pkgId) => {
                  const newPackages = packages.filter((p) => p.id !== pkgId);
                  await updateJob(job.id, { packages: newPackages });
                }}
                onPackageSkip={async (pkgId, reason) => {
                  const now = new Date().toISOString();
                  const newPackages = packages.map((p) =>
                    p.id === pkgId
                      ? {
                          ...p,
                          scheduledInError: true,
                          scheduledInErrorReason: reason,
                          scheduledInErrorAt: now,
                          scheduledInErrorBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "" },
                        }
                      : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onUndoPackageSkip={async (pkgId) => {
                  const newPackages = packages.map((p) =>
                    p.id === pkgId
                      ? { ...p, scheduledInError: false, scheduledInErrorReason: null, scheduledInErrorAt: null, scheduledInErrorBy: null }
                      : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onPackageHold={async (pkgId) => {
                  const now = new Date().toISOString();
                  const newPackages = packages.map((p) =>
                    p.id === pkgId
                      ? {
                          ...p,
                          held: true,
                          heldAt: now,
                          heldBy: { userId: userProfile?.id || "", userName: userProfile?.name || "", userRole: "guard" },
                          heldFromJobId: p.heldFromJobId || job.id,
                          heldFromJobNumber: p.heldFromJobNumber || job.jobNumber || "",
                        }
                      : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onUndoPackageHold={async (pkgId) => {
                  const newPackages = packages.map((p) => {
                    if (p.id !== pkgId) return p;
                    const { held, heldAt, heldBy, heldReason, heldDeliveryDate, heldDeliveryDirection, heldFromJobId, heldFromJobNumber, ...rest } = p;
                    return rest;
                  });
                  await updateJob(job.id, { packages: newPackages });
                }}
              />
            );
          })}
        </>
      )}

      {/* Complete-job footer. Enabled under exactly the same condition as
          before (canCompleteJob && inProgress); during the delivery phase it
          shows a disabled progress state so the guard always sees what's
          left. Sticky so it survives long stop lists. */}
      {job.status === "inProgress" && airportDone && (
        <div style={{
          position: "sticky", bottom: 0, padding: "14px 12px 12px",
          background: "linear-gradient(180deg, rgba(242,243,245,0), #f2f3f5 40%)",
        }}>
          {canCompleteJob ? (
            <>
              <div style={{
                background: "#E6F7EC", border: "1px solid #b3d9be", borderRadius: 10,
                padding: "12px 14px", marginBottom: 10, fontSize: 12, color: "#1a4a1a",
              }}>
                ✓ All deliveries complete. Tap below to close out this job.
              </div>
              <button
                className="g-btn g-btn-green"
                onClick={handleCompleteJob}
                disabled={completing}
                style={{ fontSize: 15 }}
              >
                {completing ? "Completing..." : "✓ Complete job"}
              </button>
            </>
          ) : (
            <button className="g-btn" disabled style={{
              fontSize: 14, background: "#dfe4e9", color: "#7d8894",
              border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", opacity: 1,
            }}>
              {remainingMine > 0
                ? `Complete job — ${remainingMine} stop${remainingMine === 1 ? "" : "s"} left`
                : "Complete job — waiting on the other guard's stops"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── ROUNDTRIP EXECUTION ────────────────────────────────────
// One run covering both directions under a single job number: collect the
// inbound pieces at the airport (first stop), work the mixed
// delivery/pickup route, then drop the collected outbound pieces back at
// the airport (last stop). The airport-collect phase mirrors
// InboundExecution — including the Malca seal flow — but only over the
// packages bound to DELIVERY stops; a package bound to a pickup stop is
// collected on the route, so it can't be "received" when the plane lands.
// The final dropoff mirrors OutboundExecution's confirm-to-complete.
function RoundtripExecution({ job, guardNumber, userProfile, locationsById = {} }) {
  const stops = job.stops || [];
  const packages = job.packages || [];
  const lastIdx = stops.length - 1;
  const startAirport = stops[0]?.type === "airport" ? stops[0] : null;
  const endAirport = lastIdx > 0 && stops[lastIdx]?.type === "airport" ? stops[lastIdx] : null;
  const midStart = startAirport ? 1 : 0;
  const midEndEx = endAirport ? lastIdx : stops.length;
  const middleStops = stops.slice(midStart, midEndEx);
  const airportDone = !startAirport || !!startAirport.completedAt;
  const endAirportDone = !!endAirport?.completedAt;
  const endAirportCovered = stopCoveredByGuard(endAirport, guardNumber);

  const [showScan, setShowScan] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Ordinal labels numbered within their own type: Delivery 1, Pickup 1, Delivery 2…
  const middleLabels = (() => {
    let p = 0, d = 0;
    return middleStops.map((s) => (s.type === "pickup" ? `Pickup ${++p}` : `Delivery ${++d}`));
  })();

  // Each guard sees only the route stops they cover (default: all). All
  // guards collect at the start airport — only the route + dropoff diverge.
  const myMiddle = middleStops
    .map((stop, i) => ({ stop, realIdx: i + midStart, label: middleLabels[i] }))
    .filter(({ stop }) => stopCoveredByGuard(stop, guardNumber));
  const middleDone = myMiddle.every(({ stop }) => stop.completedAt || stop.noShow);

  // Accordion over route stops — explicit tap wins while that stop is open;
  // otherwise auto-open the first stop still needing work.
  const [openKey, setOpenKey] = useState(null);
  const keyOf = (stop, realIdx) => stop.id || `m${realIdx}`;
  const firstOpen = myMiddle.find(({ stop }) => !stop.completedAt && !stop.noShow);
  const tappedStillOpen = myMiddle.some(({ stop, realIdx }) =>
    keyOf(stop, realIdx) === openKey && !stop.completedAt && !stop.noShow);
  const effectiveOpenKey = openKey === "__none__"
    ? null
    : (openKey != null && tappedStillOpen)
      ? openKey
      : (firstOpen ? keyOf(firstOpen.stop, firstOpen.realIdx) : null);

  // ── Airport collect (inbound pieces only) ─────────────────
  const stopById = {};
  stops.forEach((s) => { if (s.id) stopById[s.id] = s; });
  // A package flies in (must be received at the start airport) unless it's
  // bound to a pickup stop — those get collected on the route instead.
  const fliesIn = (pkg) => {
    const st = pkg.stopId ? stopById[pkg.stopId] : null;
    return !st || st.type !== "pickup";
  };
  const inboundItems = packages
    .map((pkg, pkgIdx) => ({ pkg, pkgIdx }))
    .filter(({ pkg }) => fliesIn(pkg));

  const brokerName = String(job.brokerName || "").toLowerCase();
  const sealExempt = !!userProfile?.sealPhotoExempt;
  const requiresSealCheck = brokerName.includes("malca") && !sealExempt;

  const pkgReady = (pkg) => {
    if (pkg.notOnFlight) return true;
    if (!requiresSealCheck) return !!pkg.receivedAt;
    if (!pkg.photos?.outerSeal) return false;
    if (pkg.outerSealColor === "red")   return true;
    if (pkg.outerSealColor === "green") return !!pkg.photos?.innerSeal;
    return false;
  };

  const arrivedItems  = inboundItems.filter(({ pkg }) => !pkg.notOnFlight);
  const missedItems   = inboundItems.filter(({ pkg }) =>  pkg.notOnFlight);
  const allPkgsReady  = inboundItems.every(({ pkg }) => pkgReady(pkg));

  // Group airport packages by their delivery stop, route order first.
  const deliveryStops = middleStops.filter((s) => s.type !== "pickup");
  const packageGroups = (() => {
    const groups = [];
    const seen = new Set();
    deliveryStops.forEach((stop) => {
      seen.add(stop.id);
      const items = inboundItems.filter(({ pkg }) => pkg.stopId === stop.id);
      if (items.length) groups.push({ id: stop.id, name: stop.locationName || stop.address || "Delivery", items });
    });
    const orphans = inboundItems.filter(({ pkg }) => !pkg.stopId || !seen.has(pkg.stopId));
    if (orphans.length) groups.push({ id: "unassigned", name: "Unassigned", items: orphans });
    return groups;
  })();

  const user = { id: userProfile?.id, name: userProfile?.name, role: "guard" };

  async function handleMarkMissed(pkgIdx)   { await markPackageNotOnFlight(job.id, pkgIdx, user, ""); }
  async function handleUnmarkMissed(pkgIdx) { await unmarkPackageNotOnFlight(job.id, pkgIdx, user); }

  async function handlePackageReceived(pkgIdx, received) {
    const newPackages = packages.map((pkg, i) =>
      i === pkgIdx ? {
        ...pkg,
        receivedAt: received ? new Date().toISOString() : null,
        receivedBy: received
          ? { guardId: userProfile?.id || "", guardName: userProfile?.name || "" }
          : null,
      } : pkg
    );
    await updateJob(job.id, { packages: newPackages });
  }

  async function handlePackagePhoto(pkgIdx, photoType, url) {
    const newPackages = packages.map((pkg, i) =>
      i === pkgIdx ? { ...pkg, photos: { ...pkg.photos, [photoType]: { url, takenAt: new Date().toISOString() } } } : pkg
    );
    await updateJob(job.id, { packages: newPackages });
  }

  async function handleSetSealColor(pkgIdx, color) {
    const newPackages = packages.map((pkg, i) =>
      i === pkgIdx ? { ...pkg, outerSealColor: color, sealColorSetAt: new Date().toISOString() } : pkg
    );
    await updateJob(job.id, { packages: newPackages });
  }

  async function handleLeaveAirport() {
    if (!allPkgsReady || !startAirport) return;
    const newStops = stops.map((s, i) => i === 0
      ? { ...s, completedAt: new Date().toISOString(), completedBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "", guardNumber } }
      : s);
    await updateJobStops(job.id, newStops);
  }

  // ── Route + dropoff handlers ──────────────────────────────
  async function handleCompleteStop(realIdx, photoUrl, note) {
    const newStops = stops.map((s, i) =>
      i === realIdx
        ? {
            ...s,
            completedAt: new Date().toISOString(),
            completedBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "", guardNumber },
            podPhotoUrl: photoUrl ?? null,
            note: note || s.note || "",
          }
        : s
    );
    await updateJobStops(job.id, newStops);
    if (newStops.every((s) => s.completedAt || s.noShow)) {
      await completeJob(job.id, {});
    }
  }

  async function handleSkipStop(realIdx, reason) {
    const now = new Date().toISOString();
    const newStops = stops.map((s, i) =>
      i === realIdx
        ? {
            ...s,
            noShow: true,
            noShowReason: reason,
            noShowAt: now,
            noShowBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "" },
          }
        : s
    );
    await updateJobStops(job.id, newStops);
    if (newStops.every((s) => s.completedAt || s.noShow)) {
      await completeJob(job.id, {});
    }
  }

  async function handleSwapStops(a, b) {
    // Only route stops swap — never the airport bookends.
    if (a < midStart || b < midStart || a >= midEndEx || b >= midEndEx) return;
    const newStops = [...stops];
    if (newStops[a].completedAt || newStops[b].completedAt) return;
    [newStops[a], newStops[b]] = [newStops[b], newStops[a]];
    await updateJobStops(job.id, newStops);
  }

  // Safety net: if every stop is done but the job didn't flip to complete
  // (two stops completed back-to-back), close it out — same as outbound.
  const autoCompletedRef = useRef(false);
  useEffect(() => {
    if (job.status !== "inProgress" || autoCompletedRef.current) return;
    const ss = job.stops || [];
    if (ss.length > 0 && ss.every((s) => s.completedAt || s.noShow)) {
      autoCompletedRef.current = true;
      completeJob(job.id, {}).catch((err) => {
        autoCompletedRef.current = false;
        console.warn("Roundtrip auto-complete failed", err);
      });
    }
  }, [job.stops, job.status, job.id]);

  return (
    <div>
      {/* Phase stepper — display only, derived from existing state. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 16px 4px" }}>
        {[
          { label: "Collect", num: 1, done: airportDone },
          { label: "Route", num: 2, done: middleDone },
          { label: "Dropoff", num: 3, done: endAirportDone },
          { label: "Done", num: 4, done: job.status === "complete" },
        ].map((ph, i, arr) => {
          const phActive = !ph.done && arr.slice(0, i).every((p) => p.done);
          return (
            <React.Fragment key={ph.label}>
              {i > 0 && (
                <div style={{ flex: 1, height: 2, borderRadius: 1, background: arr[i - 1].done ? "#2d7a2d" : "#e3e8ef" }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  background: ph.done ? "#2d7a2d" : phActive ? "#185FA5" : "#fff",
                  border: ph.done || phActive ? "none" : "2px solid #d5dce5",
                  color: "#fff", fontSize: 11, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{ph.done ? "✓" : ph.num}</div>
                <span style={{ fontSize: 12, fontWeight: phActive ? 800 : 600, color: ph.done ? "#2d7a2d" : phActive ? "#0B1C2E" : "#98a4b3" }}>
                  {ph.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Start airport — collect the inbound pieces */}
      {startAirport && (
        <>
          <div className="g-section-label">Airport — collect arriving packages</div>
          <div className="g-card" style={{ margin: "8px 12px" }}>
            <div style={{ padding: "12px 14px" }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                🛬 {startAirport.locationName || "Airport"} {airportDone && <span style={{ color: "#2d7a2d", fontSize: 12 }}>✓ Done</span>}
              </div>
              {startAirport.address && <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>{startAirport.address}</div>}
              {startAirport.accessNotes && (
                <div style={{ background: "#FFF8E6", border: "1px solid #EF9F27", borderRadius: 7, padding: "8px 10px", fontSize: 11, marginBottom: 10 }}>
                  📌 {startAirport.accessNotes}
                </div>
              )}

              {!airportDone && inboundItems.length === 0 && (
                <div style={{ fontSize: 12, color: "#aaa", textAlign: "center", padding: "10px 0" }}>
                  No arriving packages on this job — head out when ready
                </div>
              )}

              {!airportDone && packageGroups.map((group) => (
                <div key={group.id}>
                  <div style={{
                    fontSize: 11, fontWeight: 800, color: "#185FA5",
                    textTransform: "uppercase", letterSpacing: 0.4, margin: "12px 2px 6px",
                  }}>
                    📍 {group.name} <span style={{ color: "#888", fontWeight: 600 }}>· {group.items.length} pkg</span>
                  </div>
                  {group.items.map(({ pkg, pkgIdx }) => {
                    const hasOuter = !!pkg.photos?.outerSeal;
                    const hasInner = !!pkg.photos?.innerSeal;
                    const color    = pkg.outerSealColor;
                    const ready    = pkgReady(pkg);
                    const missed   = !!pkg.notOnFlight;
                    const noPhotosYet = !hasOuter && !hasInner;

                    let progress;
                    if (!requiresSealCheck) {
                      progress = pkg.receivedAt ? "✓ Received" : "Tap to confirm received";
                    } else {
                      progress = "Step 1 of 2 — photograph outer seal";
                      if (hasOuter && !color)     progress = "Step 2 of 2 — what color was the outer seal?";
                      if (color === "red")        progress = "✓ Outer seal RED — no inner check needed";
                      if (color === "green" && !hasInner) progress = "Step 3 of 3 — photograph inner seal or bag number";
                      if (color === "green" && hasInner)  progress = "✓ Outer GREEN + inner verified";
                    }

                    return (
                      <div key={pkgIdx} className={`g-pkg${ready && !missed ? " g-pkg-complete" : ""}`}
                        style={missed ? { opacity: 0.75, borderLeft: "3px solid #a32d2d" } : undefined}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>
                              {hawbLabel(job)}: {pkg.hawbNumber || `Package ${pkgIdx + 1}`}
                            </div>
                            {!missed && (
                              <div style={{ fontSize: 11, color: ready ? "#2d7a2d" : "#888", marginTop: 2 }}>
                                {progress}
                              </div>
                            )}
                          </div>
                          {ready && !missed && <span style={{ color: "#2d7a2d", fontSize: 18 }}>✓</span>}
                        </div>

                        {missed ? (
                          <div style={{
                            background: "#fdecec", border: "1px solid #f5a5a5", borderRadius: 7,
                            padding: "8px 10px", fontSize: 12, color: "#a32d2d",
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                          }}>
                            <div>
                              <div style={{ fontWeight: 700 }}>❌ Not on flight</div>
                              <div style={{ fontSize: 10, color: "#a32d2d", opacity: 0.8 }}>
                                Marked by {pkg.notOnFlightBy?.userName || "guard"}
                              </div>
                            </div>
                            <button
                              className="g-btn g-btn-ghost g-btn-sm"
                              style={{ minHeight: 30, padding: "4px 10px", fontSize: 11 }}
                              onClick={() => handleUnmarkMissed(pkgIdx)}
                            >
                              Undo
                            </button>
                          </div>
                        ) : !requiresSealCheck ? (
                          <button
                            onClick={() => handlePackageReceived(pkgIdx, !pkg.receivedAt)}
                            className={`g-btn ${pkg.receivedAt ? "g-btn-green" : "g-btn-primary"}`}
                            style={{ width: "100%", marginBottom: 6 }}
                          >
                            {pkg.receivedAt ? "✓ Received — tap to undo" : "📦 Mark package received"}
                          </button>
                        ) : (
                          <>
                            <PhotoCapture
                              label="📦 Outer seal"
                              existing={pkg.photos?.outerSeal?.url}
                              onCapture={(url) => handlePackagePhoto(pkgIdx, "outerSeal", url)}
                              jobId={job.id}
                              stopId={`pkg_${pkgIdx}_outerSeal`}
                              guardId={userProfile?.id}
                              photoType="outerSeal"
                              style={{ marginBottom: 8 }}
                            />

                            {hasOuter && !color && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>
                                  What color is the outer seal?
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button
                                    onClick={() => handleSetSealColor(pkgIdx, "red")}
                                    style={{
                                      flex: 1, background: "#a32d2d", color: "#fff",
                                      border: "none", borderRadius: 8, padding: "10px 12px",
                                      fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 44,
                                    }}
                                  >
                                    🔴 Red
                                  </button>
                                  <button
                                    onClick={() => handleSetSealColor(pkgIdx, "green")}
                                    style={{
                                      flex: 1, background: "#2d7a2d", color: "#fff",
                                      border: "none", borderRadius: 8, padding: "10px 12px",
                                      fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 44,
                                    }}
                                  >
                                    🟢 Green
                                  </button>
                                </div>
                                <div style={{ fontSize: 10, color: "#888", marginTop: 6, lineHeight: 1.4 }}>
                                  Red = secure, no inner check. Green = open the bag and photograph the inner seal or bag number.
                                </div>
                              </div>
                            )}

                            {color === "green" && (
                              <PhotoCapture
                                label="🔒 Inner seal or bag number"
                                existing={pkg.photos?.innerSeal?.url}
                                onCapture={(url) => handlePackagePhoto(pkgIdx, "innerSeal", url)}
                                jobId={job.id}
                                stopId={`pkg_${pkgIdx}_innerSeal`}
                                guardId={userProfile?.id}
                                photoType="innerSeal"
                                style={{ marginBottom: 6 }}
                              />
                            )}

                            {color && (
                              <button
                                className="g-btn g-btn-ghost g-btn-sm"
                                style={{ marginTop: 4, fontSize: 11, minHeight: 30 }}
                                onClick={() => handleSetSealColor(pkgIdx, null)}
                              >
                                Change seal color
                              </button>
                            )}

                            {noPhotosYet && (
                              <button
                                className="g-btn g-btn-ghost g-btn-sm"
                                style={{
                                  marginTop: 4, fontSize: 11, color: "#a32d2d",
                                  borderColor: "#e8a5a5", minHeight: 32,
                                }}
                                onClick={() => handleMarkMissed(pkgIdx)}
                              >
                                ❌ This package wasn't on the flight
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {!airportDone && missedItems.length > 0 && (
                <div style={{
                  background: "#fdecec", border: "1px solid #f5a5a5", borderRadius: 7,
                  padding: "8px 10px", fontSize: 11, color: "#a32d2d", marginTop: 8,
                }}>
                  ❌ {missedItems.length} package{missedItems.length !== 1 ? "s" : ""} not on flight
                  {arrivedItems.length > 0 && ` · ${arrivedItems.length} to deliver`}
                </div>
              )}

              {!airportDone && (
                <button
                  className="g-btn g-btn-ghost"
                  onClick={() => setShowScan(true)}
                  style={{ marginTop: 8, borderStyle: "dashed", borderWidth: 1, borderColor: "#185FA5", color: "#185FA5" }}
                >
                  ➕ Scan unexpected package
                </button>
              )}

              {!airportDone && (
                <button
                  className={`g-btn ${allPkgsReady ? "g-btn-green" : "g-btn-ghost"}`}
                  onClick={handleLeaveAirport}
                  disabled={!allPkgsReady}
                  style={{ marginTop: 8 }}
                >
                  {!allPkgsReady
                    ? `Photograph or mark each package (${inboundItems.filter(({ pkg }) => pkgReady(pkg)).length}/${inboundItems.length} ready)`
                    : inboundItems.length === 0
                      ? "✓ Leave airport — start the route"
                      : `✓ Leave airport — ${arrivedItems.length} to deliver`}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {showScan && (
        <ScanPackageModal
          jobId={job.id}
          hawbLabel={hawbLabel(job)}
          user={user}
          onClose={() => setShowScan(false)}
          onAdded={() => setShowScan(false)}
        />
      )}

      {/* Route stops — mixed deliveries and pickups, unlocked after the airport */}
      {myMiddle.length > 0 && (
        <>
          <div className="g-section-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
            <span>Route {airportDone ? "— complete in any order" : "— unlocks after airport step"}</span>
            <span style={{ color: "#a36b1d" }}>
              {myMiddle.filter(({ stop }) => stop.completedAt || stop.noShow).length} of {myMiddle.length} done
            </span>
          </div>
          {myMiddle.map(({ stop, realIdx, label }, vIdx) => {
            const isDone = !!stop.completedAt;
            const isPickup = stop.type === "pickup";
            const prevVisible = myMiddle[vIdx - 1];
            const nextVisible = myMiddle[vIdx + 1];
            return (
              <StopCard
                key={stop.id || realIdx}
                stop={stop}
                idx={realIdx}
                isDone={isDone}
                isActive={airportDone && !isDone}
                isLocked={!airportDone}
                requiresPhoto={isPickup
                  ? ((stop.guardNumbers && stop.guardNumbers.length) ? true : (guardNumber === 1 || guardNumber === 2))
                  : true}
                collapsible={airportDone && !isDone && !stop.noShow}
                expandedView={effectiveOpenKey === keyOf(stop, realIdx)}
                onToggleExpand={() => {
                  const k = keyOf(stop, realIdx);
                  setOpenKey(effectiveOpenKey === k ? "__none__" : k);
                }}
                showReorder={airportDone && !isDone}
                displayNumber={vIdx + 1}
                canMoveUp={!!prevVisible && !prevVisible.stop.completedAt}
                canMoveDown={!!nextVisible && !nextVisible.stop.completedAt}
                onMoveUp={() => prevVisible && handleSwapStops(realIdx, prevVisible.realIdx)}
                onMoveDown={() => nextVisible && handleSwapStops(realIdx, nextVisible.realIdx)}
                onComplete={(photoUrl, note) => handleCompleteStop(realIdx, photoUrl, note)}
                onAddNote={async (note) => {
                  const newStops = stops.map((s, i) => i === realIdx ? { ...s, note: note || "" } : s);
                  await updateJobStops(job.id, newStops);
                }}
                onSkip={(reason) => handleSkipStop(realIdx, reason)}
                jobId={job.id}
                guardId={userProfile?.id}
                label={label}
                locationsById={locationsById}
                hawbLabel={hawbLabel(job)}
                // Deliveries show their own packages plus any unassigned
                // arrival (guard-scanned at the airport); pickups only their
                // own — a new piece collected there is added via onAddPackage.
                stopPackages={isPickup
                  ? packages.filter((p) => p.stopId === stop.id)
                  : packages.filter((p) => p.stopId === stop.id || (!p.stopId && fliesIn(p)))}
                onPackagePhoto={async (pkgId, url) => {
                  const newPackages = packages.map((p) =>
                    p.id === pkgId ? { ...p, podPhotoUrl: url, stopId: p.stopId || stop.id } : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onAddPackage={async (stopId) => {
                  const newPkg = {
                    id: `pkg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                    stopId, hawbNumber: "", podPhotoUrl: null,
                    addedByGuard: true, addedAt: new Date().toISOString(),
                  };
                  await updateJob(job.id, { packages: [...packages, newPkg] });
                }}
                onUpdatePackageHawb={async (pkgId, hawb) => {
                  const newPackages = packages.map((p) =>
                    p.id === pkgId ? { ...p, hawbNumber: hawb } : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onRemovePackage={async (pkgId) => {
                  const newPackages = packages.filter((p) => p.id !== pkgId);
                  await updateJob(job.id, { packages: newPackages });
                }}
                onPackageSkip={async (pkgId, reason) => {
                  const now = new Date().toISOString();
                  const newPackages = packages.map((p) =>
                    p.id === pkgId
                      ? {
                          ...p,
                          scheduledInError: true,
                          scheduledInErrorReason: reason,
                          scheduledInErrorAt: now,
                          scheduledInErrorBy: { guardId: userProfile?.id || "", guardName: userProfile?.name || "" },
                        }
                      : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onUndoPackageSkip={async (pkgId) => {
                  const newPackages = packages.map((p) =>
                    p.id === pkgId
                      ? { ...p, scheduledInError: false, scheduledInErrorReason: null, scheduledInErrorAt: null, scheduledInErrorBy: null }
                      : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                // Hold only makes sense for a piece being delivered — a
                // pickup piece that can't be collected is a skip, not a hold.
                onPackageHold={isPickup ? undefined : async (pkgId) => {
                  const now = new Date().toISOString();
                  const newPackages = packages.map((p) =>
                    p.id === pkgId
                      ? {
                          ...p,
                          held: true,
                          heldAt: now,
                          heldBy: { userId: userProfile?.id || "", userName: userProfile?.name || "", userRole: "guard" },
                          heldFromJobId: p.heldFromJobId || job.id,
                          heldFromJobNumber: p.heldFromJobNumber || job.jobNumber || "",
                        }
                      : p
                  );
                  await updateJob(job.id, { packages: newPackages });
                }}
                onUndoPackageHold={isPickup ? undefined : async (pkgId) => {
                  const newPackages = packages.map((p) => {
                    if (p.id !== pkgId) return p;
                    const { held, heldAt, heldBy, heldReason, heldDeliveryDate, heldDeliveryDirection, heldFromJobId, heldFromJobNumber, ...rest } = p;
                    return rest;
                  });
                  await updateJob(job.id, { packages: newPackages });
                }}
              />
            );
          })}
        </>
      )}

      {/* Airport dropoff — locked until this guard's route stops are done. */}
      {endAirport && endAirportCovered && (
        <>
          <div className="g-section-label">
            Airport dropoff {endAirportDone ? "— done" : middleDone ? "— ready" : "— unlocks after the route"}
          </div>

          {!middleDone && !endAirportDone && (
            <div className="g-stop g-stop-locked" style={{ margin: "0 12px 6px", borderRadius: 12, padding: "12px 14px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🛫 {endAirport.locationName || "Airport dropoff"}</div>
              {endAirport.address && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{endAirport.address}</div>}
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>Finish the route first.</div>
            </div>
          )}

          {middleDone && !endAirportDone && (
            <div className="g-card" style={{ margin: "8px 12px", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
              <div style={{ padding: "14px" }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                  🛫 {endAirport.locationName || "Airport dropoff"}
                </div>
                {endAirport.address && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                      endAirport.lat && endAirport.lng ? `${endAirport.lat},${endAirport.lng}` : endAirport.address
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 12, color: "#185FA5", display: "inline-block", textDecoration: "underline", textDecorationColor: "#b9d0e6", marginBottom: 10 }}
                  >
                    {endAirport.address}  🧭
                  </a>
                )}
                <div style={{ background: "#E6F7EC", border: "1px solid #b3d9be", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#1a4a1a", marginBottom: 12 }}>
                  Route complete. Drop the collected packages at the airport, then confirm below.
                </div>
                <button
                  className="g-btn g-btn-green"
                  onClick={async () => {
                    if (!window.confirm("Is this job complete?")) return;
                    setCompleting(true);
                    await handleCompleteStop(lastIdx, null, "");
                    setCompleting(false);
                  }}
                  disabled={completing}
                  style={{ fontSize: 15 }}
                >
                  ✓ Yes — job complete
                </button>
              </div>
            </div>
          )}

          {endAirportDone && (
            <div className="g-stop g-stop-done" style={{ margin: "0 12px 6px", borderRadius: 12, padding: "12px 14px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🛫 {endAirport.locationName || "Airport dropoff"}</div>
              <div style={{ fontSize: 11, color: "#2d7a2d", marginTop: 4 }}>
                ✓ Completed {new Date(endAirport.completedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── DIRECT EXECUTION ───────────────────────────────────────
function DirectExecution({ job, guardNumber, userProfile, locationsById = {} }) {
  const bk = brokerKey(job.brokerName);
  const requiresPhoto = PHOTO_BROKERS.some((b) => bk.includes(b));
  const requiresChecklist = CHECKLIST_BROKERS.some((b) => bk.includes(b));
  const stops = job.stops || [];
  const brokerChecklist = job.brokerChecklist || [];
  // Tracks which package's HAWB the barcode scanner is filling. null
  // when scanner is closed. Tap a package row's 📷 button to open.
  const [scanningPkgId, setScanningPkgId] = useState(null);

  async function handleCompleteStop(idx, photoUrl, note) {
    const newStops = stops.map((s, i) =>
      i === idx
        ? {
            ...s,
            completedAt: new Date().toISOString(),
            // Firestore rejects undefined — coalesce optional fields. DAI
            // direct stops don't capture POD photos or notes by default,
            // so both can come in as undefined.
            podPhotoUrl: photoUrl ?? null,
            note: note ?? s.note ?? "",
          }
        : s
    );
    await updateJobStops(job.id, newStops);
    if (newStops.every((s) => s.completedAt)) await completeJob(job.id, {});
  }

  async function handleChecklistItem(stopIdx, itemIdx, checked) {
    const newStops = [...stops];
    const checklist = [...(newStops[stopIdx].checklist || brokerChecklist.map((item) => ({ item, checked: false })))];
    checklist[itemIdx] = { ...checklist[itemIdx], checked };
    newStops[stopIdx] = { ...newStops[stopIdx], checklist };
    await updateJobStops(job.id, newStops);
  }

  return (
    <div>
      {/* Phase stepper — display only, derived from existing state. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 16px 4px" }}>
        {[
          { label: "Pickup", num: 1, done: !!(stops[0]?.completedAt || stops[0]?.noShow) },
          { label: "Dropoff", num: 2, done: stops.length > 1 && stops.slice(1).every((s) => s.completedAt || s.noShow) },
          { label: "Done", num: 3, done: job.status === "complete" },
        ].map((ph, i, arr) => {
          const phActive = !ph.done && arr.slice(0, i).every((p) => p.done);
          return (
            <React.Fragment key={ph.label}>
              {i > 0 && (
                <div style={{ flex: 1, height: 2, borderRadius: 1, background: arr[i - 1].done ? "#2d7a2d" : "#e3e8ef" }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  background: ph.done ? "#2d7a2d" : phActive ? "#185FA5" : "#fff",
                  border: ph.done || phActive ? "none" : "2px solid #d5dce5",
                  color: "#fff", fontSize: 11, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{ph.done ? "✓" : ph.num}</div>
                <span style={{ fontSize: 12, fontWeight: phActive ? 800 : 600, color: ph.done ? "#2d7a2d" : phActive ? "#0B1C2E" : "#98a4b3" }}>
                  {ph.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="g-section-label">Stops</div>
      {stops.map((stop, idx) => {
        const isDone = !!stop.completedAt;
        // Position-based labels for direct jobs: first stop is the origin
        // pickup, last is the dropoff. Don't trust stop.type — older saved
        // direct jobs sometimes have both stops typed `pickup`.
        const isPickup = idx === 0 || (stops.length > 1 && idx < stops.length - 1);
        const checklist = stop.checklist || brokerChecklist.map((item) => ({ item, checked: false }));
        const allChecked = checklist.length === 0 || checklist.every((c) => c.checked);

        return (
          <div key={idx} className="g-card" style={{ margin: "8px 12px" }}>
            <div style={{ padding: "14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                  background: isDone ? "#2d7a2d" : "#185FA5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 12, fontWeight: 700,
                }}>
                  {isDone ? "✓" : idx + 1}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {isPickup ? "📦 Pickup" : "🏁 Dropoff"} — {stop.locationName || (isPickup ? "Pickup location" : "Dropoff location")}
                  </div>
                  {isDone && <span style={{ fontSize: 11, color: "#2d7a2d" }}>Completed</span>}
                </div>
              </div>

              {stop.outOfTown ? (
                <div style={{
                  background: "#fff7e0", border: "1px solid #f0c878", borderRadius: 7,
                  padding: "10px 12px", marginBottom: 10,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#7a5400", marginBottom: 4 }}>
                    🚗 OUT OF TOWN
                  </div>
                  <div style={{ fontSize: 11, color: "#7a5400", lineHeight: 1.5 }}>
                    Follow the rep {
                      job.ootFollowMinutes ? `approximately ${job.ootFollowMinutes} minutes` :
                      job.ootFollowMiles   ? `approximately ${job.ootFollowMiles} miles` :
                      "for the broker-specified time"
                    } after they leave the
                    origin, then break off. No fixed destination — mark complete
                    when you've broken contact.
                  </div>
                </div>
              ) : (
                stop.address && (
                  <div style={{ marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 12, color: "#888", flex: 1, minWidth: 0 }}>{stop.address}</div>
                    <AddressLink stop={stop} prominent />
                  </div>
                )
              )}
              <StopContactsCard stop={stop} locationsById={locationsById} />
              {stop.accessNotes && (
                <div style={{ background: "#FFF8E6", border: "1px solid #EF9F27", borderRadius: 7, padding: "8px 10px", fontSize: 11, marginBottom: 10 }}>
                  📌 {stop.accessNotes}
                </div>
              )}

              {/* Packages at this stop */}
              {stop.packages?.length > 0 && (
                <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>
                  📦 {stop.packages.map((p) => p.hawb || p).join(", ")}
                </div>
              )}

              {!isDone && (
                <>
                  {/* Checklist for applicable brokers */}
                  {requiresChecklist && isPickup && checklist.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6 }}>Checklist</div>
                      {checklist.map((item, cIdx) => (
                        <label key={cIdx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", fontSize: 13, cursor: "pointer", borderBottom: "1px solid #f8f8f8" }}>
                          <input
                            type="checkbox"
                            checked={item.checked}
                            onChange={(e) => handleChecklistItem(idx, cIdx, e.target.checked)}
                            style={{ width: 20, height: 20, flexShrink: 0, cursor: "pointer" }}
                          />
                          <span style={{ textDecoration: item.checked ? "line-through" : "none", color: item.checked ? "#aaa" : "#111" }}>
                            {item.item}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Photo capture — per-package when 2+ packages are
                      bound to this stop, single-stop-photo otherwise.
                      Per-package mode: each PhotoCapture saves directly
                      to the package's podPhotoUrl, and the stop auto-
                      completes when the LAST package gets photographed.
                      Runs at EVERY stop on a direct, not just the pickup.
                      A direct is A→B and the proof of DELIVERY is signed at
                      B — with capture only at A, a guard handed a signed
                      form at the drop-off had nowhere to photograph it and
                      the owner ended up attaching it by hand. The pickup
                      keeps its old behaviour (photo drives completion);
                      the drop-off also keeps Mark complete below, so a
                      guard with no form to photograph is never stuck. */}
                  {requiresPhoto && (() => {
                    // Per-package HAWB + POD flow. Guard types the HAWB
                    // they read off the POD form, takes a photo. Each
                    // entry becomes a package row bound to this stop —
                    // searchable later when Malca/Ferrari calls about a
                    // specific HAWB. "+ Add HAWB" lets guards register
                    // packages they discover on-site (one stop may have
                    // 1 or more).
                    const stopPkgs = (job.packages || []).filter((p) => p.stopId === stop.id);
                    const photographedCount = stopPkgs.filter((p) => p.podPhotoUrl).length;

                    async function addNewPkgAtStop() {
                      const newPkg = {
                        id: `pkg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                        stopId: stop.id,
                        hawbNumber: "",
                        podPhotoUrl: null,
                        addedByGuard: true,
                        addedAt: new Date().toISOString(),
                      };
                      await updateJob(job.id, {
                        packages: [...(job.packages || []), newPkg],
                      });
                    }
                    async function updateHawbOnPkg(pkgId, hawb) {
                      const newPackages = (job.packages || []).map((p) =>
                        p.id === pkgId ? { ...p, hawbNumber: hawb } : p
                      );
                      await updateJob(job.id, { packages: newPackages });
                    }
                    async function removePkg(pkgId) {
                      const newPackages = (job.packages || []).filter((p) => p.id !== pkgId);
                      await updateJob(job.id, { packages: newPackages });
                    }

                    return (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span>📄 POD per package ({photographedCount}/{stopPkgs.length || 0})</span>
                          <button
                            type="button"
                            onClick={addNewPkgAtStop}
                            style={{
                              background: "#eef4fa", border: "1px solid #c8dcee",
                              color: "#185FA5", borderRadius: 6,
                              padding: "3px 8px", fontSize: 10, fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >+ Add {hawbLabel(job)}</button>
                        </div>
                        {stopPkgs.length === 0 && (
                          <div style={{
                            background: "#fffbe9", border: "1px dashed #f0d878",
                            borderRadius: 7, padding: "10px 12px",
                            fontSize: 12, color: "#7a5400", marginBottom: 8,
                          }}>
                            None entered yet. Tap <strong>+ Add {hawbLabel(job)}</strong> for each
                            package on the POD form, type the {hawbNumLabel(job)}, then photograph the
                            form for each one.
                          </div>
                        )}
                        {stopPkgs.map((pkg, pi) => (
                          <div key={pkg.id || pi} style={{
                            marginBottom: 12, padding: "10px 12px",
                            background: pkg.podPhotoUrl ? "#fbfefb" : "#fff",
                            border: `1px solid ${pkg.podPhotoUrl ? "#b9dcc2" : "#ccd6e0"}`,
                            borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}>
                            <div style={{
                              fontSize: 10, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase",
                              color: pkg.podPhotoUrl ? "#2d7a2d" : "#8494ad", marginBottom: 6,
                            }}>
                              Package {pi + 1} of {stopPkgs.length}{pkg.podPhotoUrl ? " ✓" : ""}
                            </div>
                            {/* Label + scan/remove on their own row so the
                                number input below gets the card's FULL width —
                                the number must never be visually truncated. */}
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#555" }}>
                                  {hawbNumLabel(job)}
                                </span>
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button
                                    type="button"
                                    onClick={() => setScanningPkgId(pkg.id)}
                                    title="Scan the barcode on the POD form"
                                    style={{
                                      background: "#185FA5", border: "1px solid #185FA5",
                                      color: "#fff", borderRadius: 6,
                                      padding: "5px 11px", fontSize: 13, cursor: "pointer",
                                      flexShrink: 0, minHeight: 32,
                                    }}
                                  >📷 Scan</button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (pkg.podPhotoUrl && !window.confirm("Remove this package and its photo? Re-add it at the correct stop.")) return;
                                      removePkg(pkg.id);
                                    }}
                                    title={pkg.podPhotoUrl ? "Remove this package (wrong stop?)" : "Remove (no photo taken yet)"}
                                    style={{
                                      background: "none", border: "1px solid #ddd",
                                      color: "#888", borderRadius: 6, padding: "4px 10px",
                                      fontSize: 12, cursor: "pointer", minHeight: 32,
                                    }}
                                  >✕</button>
                                </div>
                              </div>
                              <input
                                value={pkg.hawbNumber || ""}
                                onChange={(e) => updateHawbOnPkg(pkg.id, e.target.value)}
                                placeholder="Type or scan from POD form"
                                inputMode={hawbInputMode(job)}
                                style={{ width: "100%", fontSize: 14, padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6 }}
                                autoComplete="off"
                              />
                            </div>
                            <PhotoCapture
                              label={pkg.podPhotoUrl ? "POD photo" : "Take POD photo"}
                              existing={pkg.podPhotoUrl}
                              onCapture={async (url) => {
                                const newPackages = (job.packages || []).map((p) =>
                                  p.id === pkg.id ? { ...p, podPhotoUrl: url } : p
                                );
                                await updateJob(job.id, { packages: newPackages });
                                const stillNeed = newPackages
                                  .filter((p) => p.stopId === stop.id)
                                  .some((p) => !p.podPhotoUrl);
                                if (!stillNeed) {
                                  await handleCompleteStop(idx, newPackages.find((p) => p.stopId === stop.id)?.podPhotoUrl || url, stop.note);
                                }
                              }}
                              jobId={job.id}
                              stopId={`stop_${idx}_pkg_${pi}`}
                              guardId={userProfile?.id}
                              photoType="pod"
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Complete button */}
                  {(!requiresPhoto || !isPickup) && (
                    <button
                      className={`g-btn ${!requiresChecklist || allChecked ? "g-btn-green" : "g-btn-ghost"}`}
                      disabled={requiresChecklist && isPickup && !allChecked}
                      onClick={() => handleCompleteStop(idx, null, stop.note)}
                    >
                      {requiresChecklist && isPickup && !allChecked
                        ? `Complete checklist first (${checklist.filter((c) => c.checked).length}/${checklist.length})`
                        : "✓ Mark complete"}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Manual job-complete button — visible once every stop is done.
          handleCompleteStop already calls completeJob automatically when the
          last stop is marked, but this is the recovery path when that
          auto-call fails (e.g. transient network, billing-compute hiccup) so
          the guard isn't stuck on a screen with no exit. */}
      {stops.length > 0 && stops.every((s) => s.completedAt) && (
        <div style={{ margin: "10px 12px 4px" }}>
          <button
            className="g-btn g-btn-green"
            style={{ fontSize: 15 }}
            onClick={async () => {
              try {
                await completeJob(job.id, {});
              } catch (err) {
                alert("Couldn't finish the job: " + (err?.message || err));
              }
            }}
          >
            ✓ Finish job
          </button>
          <div style={{ fontSize: 10, color: "#888", textAlign: "center", marginTop: 4 }}>
            All stops complete — tap to wrap up.
          </div>
        </div>
      )}
      {/* Barcode scanner overlay — opens when a package's 📷 button
          is tapped. On read, writes the value into that package's
          HAWB via the inline updateHawbOnPkg helper that handleSave-
          like closures use. Because we don't have a stable handle on
          updateHawbOnPkg outside its per-stop scope, the scanner just
          finds the package by id from the current job and writes. */}
      {scanningPkgId && (
        <BarcodeScanner
          onScan={async (text) => {
            const newPackages = (job.packages || []).map((p) =>
              p.id === scanningPkgId ? { ...p, hawbNumber: text } : p
            );
            await updateJob(job.id, { packages: newPackages });
            setScanningPkgId(null);
          }}
          onClose={() => setScanningPkgId(null)}
        />
      )}
    </div>
  );
}

// ── SHARED STOP CARD ───────────────────────────────────────
function StopCard({ stop, idx, isDone, isActive, isLocked, requiresPhoto, showReorder, canMoveUp, canMoveDown, onMoveUp, onMoveDown, onComplete, onAddNote, onSkip, jobId, guardId, label, locationsById = {},
  // Per-package POD + HAWB capture. When requiresPhoto is set, the
  // guard captures each package's HAWB # and POD photo individually
  // — matches the real-world flow where you read the HAWB off the
  // POD form, type it, then photograph the form. "Mark complete"
  // stays disabled until every entered package has a photo.
  stopPackages = [],
  onPackagePhoto,        // (pkgId, photoUrl) => Promise
  onAddPackage,          // (stopId) => Promise — creates a new package at this stop
  onUpdatePackageHawb,   // (pkgId, hawbValue) => Promise
  onRemovePackage,       // (pkgId) => Promise
  onPackageSkip,         // (pkgId, reason) => Promise — mark one package scheduled-in-error (skip its photo)
  onUndoPackageSkip,     // (pkgId) => Promise — undo a package's scheduled-in-error
  onPackageHold,         // (pkgId) => Promise — vault one package for a later job
  onUndoPackageHold,     // (pkgId) => Promise — undo a hold
  hawbLabel = "HAWB",    // broker-specific name (Receipt No. / Order No.)
  // Accordion mode (inbound deliveries). Defaults keep the old
  // always-expanded behavior for callers that don't pass these
  // (outbound pickups, direct stops). Collapsing hides the body with
  // display:none — children stay MOUNTED so an in-flight photo upload
  // or its retry state is never lost by collapsing the card.
  collapsible = false,
  expandedView = true,
  onToggleExpand = null,
  // Position in the list the guard sees (driving order). Falls back to the
  // saved index for callers that don't pass it.
  displayNumber = null,
}) {
  const hawbNum = hawbLabel === "HAWB" ? "HAWB #" : hawbLabel;
  // ↑ ↓ arrows only while the guard has "Reorder stops" switched on.
  const reorderMode = useContext(ReorderModeContext);
  // Closed, done or skipped stops render as one compact row: name, then
  // time and package count (or completion). The open stop shows everything.
  const compact = (collapsible && !expandedView) || isDone || !!stop.noShow;
  const [note, setNote] = useState(stop.note || "");
  const [showNote, setShowNote] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(stop.podPhotoUrl || null);
  const [completing, setCompleting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  // Holds the package id whose HAWB is currently being scanned via
  // the barcode camera. null = scanner closed.
  const [scanningPkgId, setScanningPkgId] = useState(null);
  // Packages whose POD is done render as a compact row; tapping one
  // re-expands the full card (for a retake or edit). Only successfully
  // uploaded packages collapse — an uploading/failed one stays open so
  // its retry UI is never hidden.
  const [expandedPkgIds, setExpandedPkgIds] = useState(() => new Set());
  const isSkipped = !!stop.noShow;

  // Per-package mode is active whenever the parent provides handlers
  // for adding packages + capturing HAWBs. The guard captures one
  // HAWB + photo per package. Stop is complete when there's at least
  // one package and every one has a photo. We don't auto-trigger
  // completion on photo capture here (unlike DirectExecution's auto-
  // complete) — the guard explicitly hits Mark complete after all PODs.
  const usePerPackageMode = requiresPhoto
    && typeof onPackagePhoto === "function"
    && typeof onAddPackage === "function";
  // A package is "resolved" once it has a POD photo OR the guard marked it
  // scheduled-in-error (skip the photo), OR held/vaulted for a later job
  // (couldn't deliver — the owner moves it onto its delivery day). The stop
  // can be completed when every package is resolved — a skipped or held
  // package no longer blocks completion.
  const pkgResolved = (p) => !!p.podPhotoUrl || !!p.scheduledInError || !!p.held;
  const allPackagePhotosTaken = usePerPackageMode
    ? (stopPackages.length > 0 && stopPackages.every(pkgResolved))
    : true;
  const resolvedCount = stopPackages.filter(pkgResolved).length;

  async function handleComplete() {
    if (usePerPackageMode) {
      if (!allPackagePhotosTaken) return;
    } else if (requiresPhoto && !photoUrl) {
      return;
    }
    setCompleting(true);
    // For per-package mode, the stop-level photoUrl is whatever the
    // first package's photo is (so workbook generation that reads
    // stop.podPhotoUrl still finds something). The full set lives on
    // the package docs.
    const stopPhoto = usePerPackageMode
      ? (stopPackages[0]?.podPhotoUrl || null)
      : photoUrl;
    await onComplete(stopPhoto, note);
    setCompleting(false);
  }

  async function handleSaveNote() {
    await onAddNote(note);
    setShowNote(false);
  }

  async function handleSkipClick() {
    const reason = window.prompt(
      "Skip this stop — why?\n\nExamples: 'Scheduled in error', 'Package not ready', 'Customer not available'",
      "Scheduled in error"
    );
    if (!reason || !reason.trim()) return;
    setSkipping(true);
    await onSkip(reason.trim());
    setSkipping(false);
  }

  async function handlePackageSkipClick(pkgId) {
    const reason = window.prompt(
      "Skip this package's photo — why?\n\nExamples: 'Scheduled in error', 'Package not on this job'",
      "Scheduled in error"
    );
    if (!reason || !reason.trim()) return;
    await onPackageSkip(pkgId, reason.trim());
  }

  async function handlePackageHoldClick(pkgId) {
    if (!window.confirm("Hold / vault this piece for a later job?\n\nIt won't be delivered on this job — the owner will move it onto its delivery day.")) return;
    await onPackageHold(pkgId);
  }

  return (
    <div
      id={stop.id ? `stop-${stop.id}` : undefined}
      // Read by the pinned "Next:" bar to find the next stop still to work.
      data-pending-stop={isActive && !isDone && !isSkipped ? "1" : undefined}
      data-stop-name={stop.locationName || label}
      className={`g-stop${isDone ? " g-stop-done" : isSkipped ? " g-stop-done" : isActive ? " g-stop-active" : isLocked ? " g-stop-locked" : ""}`}
      // Compact rows drop the .g-stop class padding so they stay one line tall.
      style={{ margin: "0 12px 6px", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden", opacity: isSkipped ? 0.7 : 1, ...(compact ? { padding: 0 } : null) }}
    >
      <div style={{ padding: compact ? "10px 14px" : "12px 14px" }}>
        <div
          onClick={collapsible && onToggleExpand ? onToggleExpand : undefined}
          style={{ display: "flex", alignItems: compact ? "center" : "flex-start", gap: 10, marginBottom: compact ? 0 : 6, cursor: collapsible ? "pointer" : undefined }}
        >
          <div style={{
            width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
            background: isDone ? "#2d7a2d" : isActive ? "#185FA5" : "#e8e8e8",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: isDone || isActive ? "#fff" : "#888", fontSize: 12, fontWeight: 700,
          }}>
            {isDone ? "✓" : (displayNumber ?? idx + 1)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{stop.locationName || label}</div>
            {/* Compact row: time and package count instead of the address. */}
            {compact && !isDone && !isSkipped && (
              <div style={{ fontSize: 12, color: "#6b7684", marginTop: 1 }}>
                {[stop.scheduledTime, stopPackages.length > 0 && `${stopPackages.length} package${stopPackages.length === 1 ? "" : "s"}`, "tap to open"].filter(Boolean).join(" · ")}
              </div>
            )}
            {!compact && stop.address && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  stop.lat && stop.lng ? `${stop.lat},${stop.lng}` : stop.address
                )}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ fontSize: 12, color: "#185FA5", marginTop: 2, display: "inline-block", textDecoration: "underline", textDecorationColor: "#b9d0e6" }}
              >
                {stop.address}  🧭
              </a>
            )}
            {!compact && stop.packages?.length > 0 && (
              <div style={{ fontSize: 11, color: "#185FA5", marginTop: 3 }}>
                📦 {stop.packages.map((p) => p.hawb || p).join(", ")}
              </div>
            )}
          </div>
          {showReorder && reorderMode && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* stopPropagation: the header is tappable in accordion mode —
                  a reorder tap must not also toggle the card. */}
              {canMoveUp && <button className="g-btn g-btn-ghost g-btn-sm" style={{ padding: "4px 8px", minHeight: 32 }} onClick={(e) => { e.stopPropagation(); onMoveUp(); }}>↑</button>}
              {canMoveDown && <button className="g-btn g-btn-ghost g-btn-sm" style={{ padding: "4px 8px", minHeight: 32 }} onClick={(e) => { e.stopPropagation(); onMoveDown(); }}>↓</button>}
            </div>
          )}
          {collapsible && (
            <div style={{ alignSelf: "center", color: "#185FA5", fontSize: 12, fontWeight: 700, padding: "0 2px" }}>
              {expandedView ? "▲" : "▼"}
            </div>
          )}
        </div>

        {/* Accordion body part 1 — hidden (not unmounted) when collapsed. */}
        <div style={{ display: !collapsible || expandedView ? undefined : "none" }}>
          {!isDone && <StopContactsCard stop={stop} locationsById={locationsById} />}
          {stop.accessNotes && !isDone && (
            <div style={{ background: "#FFF8E6", border: "1px solid #EF9F27", borderRadius: 7, padding: "7px 10px", fontSize: 11, marginBottom: 10 }}>
              📌 {stop.accessNotes}
            </div>
          )}
        </div>

        {isDone && !isSkipped && (
          <div style={{ fontSize: 11, color: "#2d7a2d", marginLeft: 36 }}>
            {/* Full receipt / order numbers — never shortened (standing rule). */}
            {stopPackages.length > 0 && (
              <div style={{ color: "#444", fontSize: 12, marginBottom: 1 }}>
                {stopPackages.map((p) => p.hawbNumber).filter(Boolean).join(", ")}
              </div>
            )}
            ✓ Completed {stop.completedAt ? new Date(stop.completedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : ""}
            {stop.note && <div style={{ color: "#888", marginTop: 2 }}>Note: {stop.note}</div>}
          </div>
        )}

        {isSkipped && (
          <div style={{ fontSize: 11, color: "#a32d2d", marginLeft: 36 }}>
            ✕ Skipped {stop.noShowAt ? new Date(stop.noShowAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : ""}
            {stop.noShowReason && <div style={{ color: "#888", marginTop: 2 }}>Reason: {stop.noShowReason}</div>}
          </div>
        )}

        {/* Accordion body part 2 — the work area. Hidden, not unmounted,
            when collapsed (keeps upload/retry state alive). */}
        <div style={{ display: !collapsible || expandedView ? undefined : "none" }}>
        {isActive && !isDone && !isSkipped && (
          <>
            {/* Per-package HAWB + POD capture. Guard reads HAWB off the
                POD form, types it, photographs the form. + Add HAWB
                creates a new entry for each package at this stop. */}
            {usePerPackageMode ? (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#111", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span>Packages</span>
                  <span style={{ color: "#a36b1d", fontSize: 11, fontWeight: 700 }}>
                    {resolvedCount} of {stopPackages.length || 0} done
                  </span>
                </div>
                {stopPackages.length === 0 && (
                  <div style={{
                    background: "#fffbe9", border: "1px dashed #f0d878",
                    borderRadius: 7, padding: "10px 12px",
                    fontSize: 12, color: "#7a5400", marginBottom: 8,
                  }}>
                    None entered yet. Tap <strong>+ Add package</strong> below for each
                    package, type the {hawbNum} from the POD form, then photograph the form.
                  </div>
                )}
                {stopPackages.map((pkg, pi) => {
                  const pkgKey = pkg.id || pi;
                  // Compact row for finished packages (successful upload
                  // only) — tap to re-open the full card for a retake.
                  if (pkg.podPhotoUrl && !expandedPkgIds.has(pkgKey)) {
                    return (
                      <div
                        key={pkgKey}
                        onClick={() => setExpandedPkgIds((prev) => { const next = new Set(prev); next.add(pkgKey); return next; })}
                        title="Tap to view or retake this package's POD"
                        style={{
                          display: "flex", alignItems: "center", gap: 10, minHeight: 52,
                          padding: "6px 10px", marginBottom: 8, cursor: "pointer",
                          background: "#fbfefb", border: "1px solid #b9dcc2", borderRadius: 10,
                        }}
                      >
                        <img src={pkg.podPhotoUrl} alt="POD" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{pkg.hawbNumber || `Package ${pi + 1}`}</div>
                          <div style={{ fontSize: 11, color: "#2d7a2d", fontWeight: 600 }}>✓ POD uploaded</div>
                        </div>
                        <span style={{ color: "#8494ad", fontSize: 13 }}>▸</span>
                      </div>
                    );
                  }
                  return (
                  <div key={pkgKey} style={{
                    marginBottom: 12, padding: "10px 12px",
                    background: pkg.podPhotoUrl ? "#fbfefb" : "#fff",
                    border: `1px solid ${pkg.podPhotoUrl ? "#b9dcc2" : "#ccd6e0"}`,
                    borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    ...(pkg.scheduledInError ? { borderLeft: "3px solid #a32d2d", opacity: 0.85 } : null),
                  }}>
                    <div style={{
                      fontSize: 10, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase",
                      color: pkg.podPhotoUrl ? "#2d7a2d" : "#8494ad", marginBottom: 6,
                    }}>
                      Package {pi + 1} of {stopPackages.length}{pkg.podPhotoUrl ? " ✓" : ""}
                    </div>
                    {/* Label + scan/remove on their own row so the number
                        input below gets the card's FULL width — the number
                        must never be visually truncated (standing rule). */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#555" }}>
                          {hawbNum}
                        </span>
                        <div style={{ display: "flex", gap: 6 }}>
                          {/* Scan only while the box is empty; once a number
                              is in, the guard edits it by typing. */}
                          {!pkg.scheduledInError && !String(pkg.hawbNumber || "").trim() && (
                            <button
                              type="button"
                              onClick={() => setScanningPkgId(pkg.id)}
                              title="Scan the barcode on the POD form"
                              style={{
                                background: "#185FA5", border: "1px solid #185FA5",
                                color: "#fff", borderRadius: 6,
                                padding: "5px 11px", fontSize: 13, cursor: "pointer",
                                flexShrink: 0, minHeight: 32,
                              }}
                            >📷 Scan</button>
                          )}
                          {onRemovePackage && (
                            <button
                              type="button"
                              onClick={() => {
                                if (pkg.podPhotoUrl && !window.confirm("Remove this package and its photo? Re-add it at the correct stop.")) return;
                                onRemovePackage(pkg.id);
                              }}
                              title={pkg.podPhotoUrl ? "Remove this package (wrong stop?)" : "Remove (no photo taken yet)"}
                              style={{
                                background: "none", border: "1px solid #ddd",
                                color: "#888", borderRadius: 6, padding: "4px 10px",
                                fontSize: 12, cursor: "pointer", minHeight: 32,
                              }}
                            >✕</button>
                          )}
                        </div>
                      </div>
                      <input
                        value={pkg.hawbNumber || ""}
                        onChange={(e) => onUpdatePackageHawb && onUpdatePackageHawb(pkg.id, e.target.value)}
                        placeholder="Type or scan from POD form"
                        inputMode={hawbLabel === "Receipt No." ? "numeric" : "text"}
                        disabled={!!pkg.scheduledInError}
                        style={{ width: "100%", fontSize: 14, padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6 }}
                        autoComplete="off"
                      />
                    </div>
                    {pkg.held ? (
                      // Vaulted for a later job — couldn't deliver today. Owner
                      // moves it onto its delivery-day job from the office.
                      <div style={{
                        background: "#fffbe9", border: "1px solid #f0d878", borderRadius: 7,
                        padding: "8px 10px", fontSize: 12, color: "#7a5400",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      }}>
                        <div style={{ fontWeight: 700 }}>🔒 Held — vaulted for a later job</div>
                        {onUndoPackageHold && (
                          <button
                            className="g-btn g-btn-ghost g-btn-sm"
                            style={{ minHeight: 30, padding: "4px 10px", fontSize: 11 }}
                            onClick={() => onUndoPackageHold(pkg.id)}
                          >
                            Undo
                          </button>
                        )}
                      </div>
                    ) : pkg.scheduledInError ? (
                      // Photo skipped for this package — guard flagged it as
                      // scheduled in error. Mirrors the inbound "not on flight"
                      // state: red banner + Undo.
                      <div style={{
                        background: "#fdecec", border: "1px solid #f5a5a5", borderRadius: 7,
                        padding: "8px 10px", fontSize: 12, color: "#a32d2d",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      }}>
                        <div>
                          <div style={{ fontWeight: 700 }}>✕ Scheduled in error — photo skipped</div>
                          {pkg.scheduledInErrorReason && (
                            <div style={{ fontSize: 10, color: "#a32d2d", opacity: 0.8 }}>
                              Reason: {pkg.scheduledInErrorReason}
                            </div>
                          )}
                        </div>
                        {onUndoPackageSkip && (
                          <button
                            className="g-btn g-btn-ghost g-btn-sm"
                            style={{ minHeight: 30, padding: "4px 10px", fontSize: 11 }}
                            onClick={() => onUndoPackageSkip(pkg.id)}
                          >
                            Undo
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <PhotoCapture
                          label={pkg.podPhotoUrl ? "POD photo" : "Take POD photo"}
                          existing={pkg.podPhotoUrl}
                          onCapture={(url) => onPackagePhoto(pkg.id, url)}
                          jobId={jobId}
                          stopId={`stop_${idx}_pkg_${pi}`}
                          guardId={guardId}
                          photoType="pod"
                        />
                        {/* Per-package "scheduled in error" — skip just this
                            package's photo. Only offered before a photo is
                            taken; once photographed there's nothing to skip. */}
                        {onPackageSkip && !pkg.podPhotoUrl && (
                          <button
                            type="button"
                            className="g-btn g-btn-ghost g-btn-sm"
                            onClick={() => handlePackageSkipClick(pkg.id)}
                            style={{ marginTop: 6, fontSize: 11, color: "#a32d2d", borderColor: "#e8a5a5", minHeight: 32, width: "100%" }}
                          >
                            ✕ Scheduled in error — skip photo
                          </button>
                        )}
                        {/* Couldn't deliver — vault it for a later job. Only
                            before a photo (a delivered piece isn't held). */}
                        {onPackageHold && !pkg.podPhotoUrl && (
                          <button
                            type="button"
                            className="g-btn g-btn-ghost g-btn-sm"
                            onClick={() => handlePackageHoldClick(pkg.id)}
                            style={{ marginTop: 6, fontSize: 11, color: "#7a5400", borderColor: "#f0d878", minHeight: 32, width: "100%" }}
                          >
                            🔒 Hold / vault for later
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => onAddPackage(stop.id)}
                  style={{
                    width: "100%", minHeight: 44, marginBottom: 4,
                    background: "#fff", border: "2px dashed #c8dcee",
                    color: "#185FA5", borderRadius: 10,
                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}
                >+ Add package — {hawbNum}</button>
              </div>
            ) : (
              requiresPhoto && (
                <PhotoCapture
                  label="Take POD photo"
                  existing={photoUrl}
                  onCapture={setPhotoUrl}
                  jobId={jobId}
                  stopId={`stop_${idx}`}
                  guardId={guardId}
                  photoType="pod"
                  style={{ marginBottom: 8 }}
                />
              )
            )}

            {showNote ? (
              <div style={{ marginBottom: 8 }}>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note for this stop..."
                  style={{ width: "100%", minHeight: 60, fontSize: 13, borderRadius: 8, border: "1px solid #ddd", padding: 8 }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button className="g-btn g-btn-ghost g-btn-sm" style={{ flex: 1 }} onClick={() => setShowNote(false)}>Cancel</button>
                  <button className="g-btn g-btn-primary g-btn-sm" style={{ flex: 1 }} onClick={handleSaveNote}>Save note</button>
                </div>
              </div>
            ) : (
              <button className="g-btn g-btn-ghost g-btn-sm" style={{ marginBottom: 8, minHeight: 36 }} onClick={() => setShowNote(true)}>
                {note ? `📝 ${note.slice(0, 30)}${note.length > 30 ? "…" : ""}` : "+ Add note"}
              </button>
            )}

            <button
              className={`g-btn ${
                usePerPackageMode
                  ? (allPackagePhotosTaken ? "g-btn-green" : "g-btn-ghost")
                  : (requiresPhoto && !photoUrl ? "g-btn-ghost" : "g-btn-green")
              }`}
              onClick={handleComplete}
              disabled={
                (usePerPackageMode && !allPackagePhotosTaken)
                || (!usePerPackageMode && requiresPhoto && !photoUrl)
                || completing
                || skipping
              }
            >
              {completing
                ? "Saving..."
                : usePerPackageMode && !allPackagePhotosTaken
                  ? `${resolvedCount}/${stopPackages.length} done — photograph or skip each to complete`
                  : !usePerPackageMode && requiresPhoto && !photoUrl
                    ? "Photo required to complete"
                    : "✓ Mark complete"}
            </button>

            {onSkip && (
              <button
                className="g-btn g-btn-ghost g-btn-sm"
                onClick={handleSkipClick}
                disabled={completing || skipping}
                style={{ marginTop: 6, fontSize: 11, color: "#a32d2d", borderColor: "#e8a5a5", minHeight: 32 }}
              >
                {skipping ? "Skipping..." : "✕ Scheduled in error — skip (no photo)"}
              </button>
            )}
          </>
        )}
        </div>
      </div>
      {/* Barcode scanner overlay — opens when a package's 📷 button is
          tapped. On successful read, writes the value into that
          package's HAWB and closes. */}
      {scanningPkgId && (
        <BarcodeScanner
          onScan={(text) => {
            if (onUpdatePackageHawb) onUpdatePackageHawb(scanningPkgId, text);
            setScanningPkgId(null);
          }}
          onClose={() => setScanningPkgId(null)}
        />
      )}
    </div>
  );
}

// ── PHOTO CAPTURE ──────────────────────────────────────────
function PhotoCapture({ label, existing, onCapture, jobId, stopId, guardId, photoType, style = {}, autoComplete = false }) {
  const photoMeta = useContext(PhotoMetaContext);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existing || null);
  const [cropFile, setCropFile] = useState(null); // File pending crop; when set, cropper modal is open
  const [pendingFile, setPendingFile] = useState(null); // File that failed to upload, kept for retry
  const [uploadError, setUploadError] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const inputRef = useRef();

  // Only POD photos get the cropper. Other captures (check-in selfies, etc.)
  // pass straight through.
  const shouldCrop = photoType === "pod";

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (shouldCrop) setCropFile(file);
    else uploadAndCapture(file);
  }

  // Try the upload up to N times with brief backoff. Photos on a moving phone
  // hit flaky cell connections constantly, so a single failure shouldn't lose
  // the shot. After exhausting retries, keep the file in memory so the guard
  // can tap "Retry upload" without re-taking the photo.
  async function uploadAndCapture(file) {
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);
    setUploadError(null);
    setPendingFile(file);
    const maxAttempts = 3;
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      setAttemptCount(attempt);
      try {
        const url = await uploadGuardPhoto(file, jobId, stopId, guardId, photoType);
        setPreview(url);
        setPendingFile(null);
        setUploadError(null);
        onCapture(url);
        // Record to the owner's live photo feed. companyId is required by the
        // Firestore rules; jobNumber/guardName come from context so the feed
        // shows who/what. Fire-and-forget — a feed-write failure must never
        // block or fail the capture.
        logPhoto({
          jobId,
          companyId: photoMeta.companyId || null,
          jobNumber: photoMeta.jobNumber || "",
          guardName: photoMeta.guardName || "",
          url, type: photoType, stopId, guardId,
        }).catch((e) => console.warn("logPhoto failed", e));
        setUploading(false);
        return;
      } catch (err) {
        lastErr = err;
        console.warn(`Photo upload attempt ${attempt}/${maxAttempts} failed:`, err);
        if (attempt < maxAttempts) {
          // 1s, then 2s — quick enough to not feel stuck.
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, attempt * 1000));
        }
      }
    }
    // All retries exhausted. Surface the error and keep `pendingFile` for
    // manual retry. We do NOT call onCapture here — that would falsely tell
    // the parent the upload succeeded.
    setUploadError(lastErr?.message || "Upload failed. Check signal and try again.");
    setUploading(false);
  }

  function retryUpload() {
    if (pendingFile) uploadAndCapture(pendingFile);
  }

  return (
    <div style={style}>
      {/* No `capture` → guard can take a new photo OR pick an existing one from their library */}
      <input ref={inputRef} type="file" accept="image/*"
        style={{ display: "none" }} onChange={handleFile} />
      <button
        className={`g-photo-btn${preview && !uploadError ? " done" : ""}${uploadError && !uploading ? " failed" : ""}`}
        onClick={() => inputRef.current?.click()}
        type="button"
        disabled={uploading}
      >
        {uploading
          ? `Uploading${attemptCount > 1 ? ` (retry ${attemptCount}/3)` : "..."}`
          : uploadError
            ? `⚠️ Retake ${label}`
            : preview ? `✓ ${label} — retake` : `📷 ${label}`}
      </button>
      {preview && !uploading && (
        <img src={preview} alt="capture" className="g-photo-thumb" />
      )}
      {uploadError && !uploading && (
        <div style={{
          marginTop: 8, padding: "10px 12px",
          background: "#fff7e0", border: "1px solid #f0c878", borderRadius: 7,
          fontSize: 11, color: "#7a5400",
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Upload failed</div>
          <div style={{ marginBottom: 8 }}>{uploadError}</div>
          <button
            type="button"
            onClick={retryUpload}
            style={{
              background: "#a36b1d", color: "#fff", border: "none", borderRadius: 6,
              padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >🔄 Retry upload</button>
        </div>
      )}
      {cropFile && (
        <PodCropper
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onSkip={(f) => { setCropFile(null); uploadAndCapture(f); }}
          onDone={(f) => { setCropFile(null); uploadAndCapture(f); }}
        />
      )}
    </div>
  );
}

// ── ADD PICKUP IN FIELD ────────────────────────────────────
// Lets a guard add an unscheduled pickup on the fly (e.g., a last-minute
// customer call). The stop is inserted before the airport on outbound jobs.
function AddPickupModal({
  onClose,
  onAdd,
  existingStops = [],
  existingLocations = {},
  onAddPackageToExistingStop,
  jobId,
  guardId,
  hawbNum = "HAWB #",    // broker-specific name (Receipt No. / Order No.)
}) {
  // Two paths in one modal:
  //   A) Forgot a package at a pickup that's already on the job —
  //      reuse the stop, save a new package row under that stopId,
  //      capture POD inline. Pickup time = the stop's completedAt
  //      (or now if it wasn't completed yet).
  //   B) Brand-new pickup stop the field threw at us — same flow as
  //      before, but the location-name field autocompletes off the
  //      company's saved locations directory so a known address can
  //      prefill without re-typing.
  const [mode, setMode] = useState(existingStops.length > 0 ? "existing" : "new");

  // Existing-stop path
  const [forgottenStopId, setForgottenStopId] = useState("");
  const [forgottenHawb, setForgottenHawb] = useState("");
  const [forgottenPodUrl, setForgottenPodUrl] = useState(null);
  const [addingForgotten, setAddingForgotten] = useState(false);

  // New-stop path
  const [locationName, setLocationName] = useState("");
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [accessNotes, setAccessNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Name suggestions off the company's locations directory. Show
  // up to 5 partial matches once the guard has typed at least 2
  // chars — fewer than that is too noisy to be useful.
  const locList = useMemo(
    () => Object.values(existingLocations || {}).filter((l) => l && l.name),
    [existingLocations]
  );
  const nameSuggestions = useMemo(() => {
    const q = locationName.trim().toLowerCase();
    if (q.length < 2) return [];
    return locList
      .filter((l) => (l.name || "").toLowerCase().includes(q))
      .slice(0, 5);
  }, [locationName, locList]);

  function pickSuggestion(loc) {
    setLocationName(loc.name || "");
    setAddress(loc.address || "");
    setLat(loc.lat ?? null);
    setLng(loc.lng ?? null);
    setAccessNotes(loc.accessNotes || "");
  }

  async function handleSubmit() {
    if (!locationName.trim() && !address.trim()) {
      window.alert("Enter at least a location name or address.");
      return;
    }
    setSaving(true);
    await onAdd({
      locationName: locationName.trim(),
      address: address.trim(),
      lat,
      lng,
      accessNotes: accessNotes.trim(),
    });
    setSaving(false);
  }

  async function handleAddForgotten() {
    if (!forgottenStopId) {
      window.alert("Pick which stop this package was at.");
      return;
    }
    setAddingForgotten(true);
    await onAddPackageToExistingStop({
      stopId: forgottenStopId,
      hawbNumber: forgottenHawb.trim(),
      podPhotoUrl: forgottenPodUrl,
    });
    setAddingForgotten(false);
  }

  function fmtTime(iso) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
    } catch { return ""; }
  }

  return (
    <div className="g-modal-overlay" onClick={onClose}>
      <div
        className="g-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "92vh", overflowY: "auto" }}
      >
        <div className="g-modal-title">➕ Add package or pickup</div>

        {/* Mode toggle — defaults to "existing" if there are any stops
            already on the job (most common case). */}
        {existingStops.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12, background: "#f0f3f7", padding: 4, borderRadius: 8 }}>
            <button
              type="button"
              onClick={() => setMode("existing")}
              style={{
                flex: 1, padding: "8px 10px", border: "none", borderRadius: 6,
                background: mode === "existing" ? "#185FA5" : "transparent",
                color: mode === "existing" ? "#fff" : "#555",
                fontWeight: 700, fontSize: 12, cursor: "pointer",
              }}
            >
              Same location as a stop
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              style={{
                flex: 1, padding: "8px 10px", border: "none", borderRadius: 6,
                background: mode === "new" ? "#185FA5" : "transparent",
                color: mode === "new" ? "#fff" : "#555",
                fontWeight: 700, fontSize: 12, cursor: "pointer",
              }}
            >
              New location
            </button>
          </div>
        )}

        {mode === "existing" && existingStops.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 12, color: "#666" }}>
              Add a package to a pickup that's already on this job — useful when
              you find a second package at a stop after you've moved on.
              Pickup time uses the stop's existing time stamp.
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>
                Which stop?
              </label>
              <select
                value={forgottenStopId}
                onChange={(e) => setForgottenStopId(e.target.value)}
                style={{ width: "100%", fontSize: 14, padding: "10px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fff" }}
              >
                <option value="">— Pick stop —</option>
                {existingStops.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.locationName || "Pickup"}
                    {s.completedAt ? ` · ✓ ${fmtTime(s.completedAt)}` : " · not yet done"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>
                {hawbNum} (optional)
              </label>
              <input
                type="text"
                value={forgottenHawb}
                onChange={(e) => setForgottenHawb(e.target.value)}
                placeholder="Type or read from POD form"
                inputMode={hawbNum === "Receipt No." ? "numeric" : "text"}
                style={{ width: "100%", fontSize: 14, padding: "10px 12px", borderRadius: 8, border: "1px solid #ddd" }}
                autoComplete="off"
              />
            </div>
            <PhotoCapture
              label={forgottenPodUrl ? "POD photo" : "Take POD photo"}
              existing={forgottenPodUrl}
              onCapture={setForgottenPodUrl}
              jobId={jobId}
              stopId={`forgotten_${forgottenStopId || "x"}_${Date.now()}`}
              guardId={guardId}
              photoType="pod"
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
              <button
                className="g-btn g-btn-green"
                onClick={handleAddForgotten}
                disabled={addingForgotten || !forgottenStopId}
              >
                {addingForgotten ? "Adding..." : "✓ Add package to this stop"}
              </button>
              <button className="g-btn g-btn-ghost" onClick={onClose} disabled={addingForgotten}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === "new" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 12, color: "#666" }}>
              Adding a pickup in the field — the owner will see this stop immediately.
            </div>
            <div style={{ position: "relative" }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>
                Location name
              </label>
              <input
                type="text"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="e.g., Diamonds Direct"
                style={{ width: "100%", fontSize: 14, padding: "10px 12px", borderRadius: 8, border: "1px solid #ddd" }}
                autoComplete="off"
              />
              {nameSuggestions.length > 0 && (
                <div
                  style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
                    background: "#fff", border: "1px solid #ddd", borderRadius: 8,
                    marginTop: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    maxHeight: 220, overflowY: "auto",
                  }}
                >
                  {nameSuggestions.map((loc) => (
                    <div
                      key={loc.id}
                      onMouseDown={(e) => { e.preventDefault(); pickSuggestion(loc); }}
                      style={{
                        padding: "8px 12px", cursor: "pointer", fontSize: 13,
                        borderBottom: "1px solid #f4f4f4",
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{loc.name}</div>
                      {loc.address && (
                        <div style={{ fontSize: 11, color: "#888" }}>{loc.address}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>
                Address
              </label>
              <PlacesAutocomplete
                value={address}
                onChange={(addr, latV, lngV) => {
                  setAddress(addr);
                  setLat(latV);
                  setLng(lngV);
                }}
                placeholder="Start typing an address..."
                style={{ width: "100%", fontSize: 14, padding: "10px 12px", borderRadius: 8, border: "1px solid #ddd" }}
              />
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>
                Access notes (optional)
              </label>
              <textarea
                value={accessNotes}
                onChange={(e) => setAccessNotes(e.target.value)}
                placeholder="Suite #, gate code, contact name..."
                style={{ width: "100%", minHeight: 60, fontSize: 13, borderRadius: 8, border: "1px solid #ddd", padding: 8 }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
              <button
                className="g-btn g-btn-green"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? "Adding..." : "✓ Add pickup"}
              </button>
              <button className="g-btn g-btn-ghost" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
